import { EncTopByFieldCondition, } from './Encodings/EncFilter';
import { AllowedAutoMarkChannels, AllowedMarkChannels, MarkType, } from './Encodings/MarkType';
import { EncodedTotals } from './Encoders/Totals';
import { BField, VariableType } from '../Model/Field';
import { FilterType, SelectionMode } from '../Model/Filter';
import { SortType } from '../Model/Sort';
import { SystemFieldName } from '../Model/SystemField';
import { EncFieldGroup } from './Encodings/EncFieldGroup';
import { ExprFrame } from './Encodings/ExprFrame';
import { toContinuousDateFilterField } from './RelativeDateFilter';
class EncodedVariable {
    constructor(variable) {
        this.type = variable.type;
        this.level = variable.level;
        this.fixed = variable.fixed;
    }
    serialize() {
        return {
            type: this.type,
            level: this.level,
            fixed: this.fixed,
        };
    }
}
export class EncodedField {
    static matches(f1, f2) {
        return (f1.variable.type === f2.variable.type &&
            f1.formula === f2.formula &&
            ExprFrame.equals(f1.frame, f2.frame));
    }
    constructor(field, parent) {
        this.parent = parent;
        if (!field.id) {
            throw Error('id is required');
        }
        if (!field.formula) {
            throw Error('formula is required');
        }
        if (!field.variable) {
            throw Error('variable is required');
        }
        this.id = field.id;
        this.formula = field.formula;
        this.variable = new EncodedVariable(field.variable);
        this.frame = field.frame;
        this.groupId = field.groupId;
    }
    get isContinuous() {
        return this.variable.type === VariableType.CONTINUOUS;
    }
    /**
     * @deprecated If you are using this property just know that it is NOT
     * guaranteed to accurately distinguish between dimensions and measures.
     * A formula of [some_agg_calc] would return a false-positive as Vegas
     * does not know that the underlying calc formula contains an aggregation
     */
    get isDimension() {
        // Nice and hacky
        return this.formula.startsWith('[') || this.formula.startsWith('DATE');
    }
    get isDiscrete() {
        return this.variable.type === VariableType.DISCRETE;
    }
    get isInGroup() {
        return this.groupId != null;
    }
    swapFrameAddress() {
        var _a;
        const frameType = (_a = this.frame) === null || _a === void 0 ? void 0 : _a.type;
        if (frameType === ExprFrame.Type.Table) {
            this.frame = ExprFrame.Table.make(getOppositeFrameAddress(this.frame.address));
        }
        else if (frameType === ExprFrame.Type.Pane) {
            this.frame = ExprFrame.Pane.make(getOppositeFrameAddress(this.frame.address));
        }
    }
    clone(parent) {
        return new EncodedField({
            ...this.serialize(),
            id: BField.GenerateID(),
        }, parent);
    }
    detach() {
        if (this.parent instanceof EncodedSingleMark) {
            this.parent.values = undefined;
        }
        else if (this.parent instanceof EncodedSort) {
            this.parent.byField = undefined;
        }
        else {
            this.parent.removeField(this.id);
        }
    }
    serialize() {
        return {
            id: this.id,
            formula: this.formula,
            variable: this.variable.serialize(),
            frame: this.frame,
            groupId: this.groupId,
        };
    }
}
function getOppositeFrameAddress(address) {
    switch (address) {
        case ExprFrame.Address.Across:
            return ExprFrame.Address.Down;
        case ExprFrame.Address.AcrossDown:
            return ExprFrame.Address.DownAcross;
        case ExprFrame.Address.Down:
            return ExprFrame.Address.Across;
        case ExprFrame.Address.DownAcross:
            return ExprFrame.Address.AcrossDown;
        default:
            throw new Error(`Unhandled frame address: ${address}`);
    }
}
class EncodedDualAxisField extends EncodedField {
    constructor(field, axis, other, parent) {
        var _a;
        super(field, parent);
        this.axis = axis;
        this.other = other;
        const otherId = (_a = other.groupId) !== null && _a !== void 0 ? _a : other.id;
        if (axis === 'x') {
            this.x = otherId;
        }
        else {
            this.y = otherId;
        }
    }
    /**
     * Returns x | y depending on the axis
     */
    get ref() {
        return this.axis === 'x' ? this.x : this.y;
    }
    /**
     * Sets x | y depending on the axis
     */
    set ref(reference) {
        if (this.axis === 'x') {
            this.x = reference;
        }
        else {
            this.y = reference;
        }
    }
    serialize() {
        const field = super.serialize();
        if (this.axis === 'x') {
            return {
                ...field,
                x: this.x,
            };
        }
        else {
            return {
                ...field,
                y: this.y,
            };
        }
    }
}
class EncodedSingleMark {
    constructor(field, lookup) {
        if (field) {
            this.values = new EncodedField(field, this);
            lookup.set(this.values.id, this.values);
        }
    }
    removeFromLookup(lookup) {
        if (this.values) {
            lookup.delete(this.values.id);
        }
    }
    serialize() {
        return {
            ...(this.values ? { values: this.values.serialize() } : {}),
        };
    }
}
class EncodedMultiMark {
    constructor(fields, lookup) {
        this.lookup = lookup;
        this.values = new EncodedFieldArray(fields, lookup, false);
    }
    insertField(field, at) {
        const encoded = new EncodedField(field, this.values);
        return this.insertEncodedField(encoded, at);
    }
    insertEncodedField(field, at) {
        const sameField = this.values.fields.find((f) => EncodedField.matches(f, field));
        if (sameField) {
            this.values.removeField(sameField.id);
        }
        this.values.insertField(field, at);
        this.lookup.set(field.id, field);
        return field;
    }
    removeFromLookup(lookup) {
        if (this.values.length) {
            for (const val of this.values.fields) {
                lookup.delete(val.id);
            }
        }
    }
    serialize() {
        return {
            values: this.values.serialize(),
        };
    }
}
class EncodedBaseline extends EncodedMultiMark {
    constructor(baseline, lookup) {
        if (baseline) {
            super(baseline.values, lookup);
            this.value = baseline.value;
        }
    }
    serialize() {
        return {
            ...super.serialize(),
            value: this.value,
        };
    }
}
class EncodedColor extends EncodedMultiMark {
    constructor(color, lookup) {
        if (color) {
            super(color.values, lookup);
            this.value = color.value;
            this.transparency = color.transparency;
            this.target = color.target;
        }
    }
    serialize() {
        return {
            ...super.serialize(),
            value: this.value,
            transparency: this.transparency,
            target: this.target,
        };
    }
}
class EncodedAngle extends EncodedMultiMark {
    constructor(size, lookup) {
        super(size.values, lookup);
    }
    serialize() {
        return {
            ...super.serialize(),
        };
    }
}
class EncodedSize extends EncodedMultiMark {
    constructor(size, lookup) {
        super(size.values, lookup);
        this.value = size.value;
    }
    serialize() {
        return {
            value: this.value,
            ...super.serialize(),
        };
    }
}
class EncodedLabel extends EncodedMultiMark {
    constructor(label, lookup) {
        super(label.values, lookup);
    }
    serialize() {
        return {
            ...super.serialize(),
        };
    }
}
export class EncodedText extends EncodedMultiMark {
    constructor(text, lookup) {
        super(text.values, lookup);
        if (text.display != null) {
            this.display = {
                useLabelThinning: text.display.useLabelThinning,
                showAll: text.display.showAll,
                showFirst: text.display.showFirst,
                showLast: text.display.showLast,
                showMax: text.display.showMax,
                showMin: text.display.showMin,
            };
        }
    }
    serialize() {
        var _a, _b, _c, _d, _e, _f;
        const display = this.display != null
            ? {
                useLabelThinning: (_a = this.display) === null || _a === void 0 ? void 0 : _a.useLabelThinning,
                showAll: (_b = this.display) === null || _b === void 0 ? void 0 : _b.showAll,
                showFirst: (_c = this.display) === null || _c === void 0 ? void 0 : _c.showFirst,
                showLast: (_d = this.display) === null || _d === void 0 ? void 0 : _d.showLast,
                showMax: (_e = this.display) === null || _e === void 0 ? void 0 : _e.showMax,
                showMin: (_f = this.display) === null || _f === void 0 ? void 0 : _f.showMin,
            }
            : undefined;
        return {
            ...super.serialize(),
            display,
        };
    }
}
class EncodedDetail extends EncodedMultiMark {
    constructor(detail, lookup) {
        super(detail.values, lookup);
    }
    serialize() {
        return {
            ...super.serialize(),
        };
    }
}
class EncodedTooltip extends EncodedMultiMark {
    constructor(tooltip, lookup) {
        super(tooltip.values, lookup);
    }
    serialize() {
        return {
            ...super.serialize(),
        };
    }
}
class EncodedMarkSettings {
    constructor(type) {
        this.value = type.value || null;
        this.automatic = !!type.automatic;
    }
    serialize() {
        return {
            value: this.value,
            automatic: this.automatic,
        };
    }
}
class EncodedMarks {
    constructor(mark, lookup) {
        var _a;
        this.lookup = lookup;
        this.type = new EncodedMarkSettings(mark.type || { automatic: true });
        this.color = new EncodedColor(mark.color || {}, lookup);
        this.size = new EncodedSize(mark.size || {}, lookup);
        this.text = new EncodedText(mark.text || {}, lookup);
        this.label = new EncodedLabel(mark.label || {}, lookup);
        this.detail = new EncodedDetail(mark.detail || {}, lookup);
        this.angle = new EncodedAngle(mark.angle || {}, lookup);
        this.tooltip = new EncodedTooltip(mark.tooltip || {}, lookup);
        this.baseline = new EncodedBaseline(mark.baseline || {}, lookup);
        this.options = { ...((_a = mark.options) !== null && _a !== void 0 ? _a : {}) };
    }
    insertField(field, channel, at) {
        switch (channel) {
            case 'baseline':
                this.baseline.removeFromLookup(this.lookup);
                this.baseline.values.fields.length = 0;
                return this.baseline.insertField(field, at);
            case 'color':
                this.color.removeFromLookup(this.lookup);
                this.color.values.fields.length = 0;
                return this.color.insertField(field, at);
            case 'size':
                this.size.removeFromLookup(this.lookup);
                this.size.values.fields.length = 0;
                return this.size.insertField(field, at);
            case 'label':
                return this.label.insertField(field, at);
            case 'text':
                return this.text.insertField(field, at);
            case 'detail':
                return this.detail.insertField(field, at);
            case 'angle':
                this.angle.removeFromLookup(this.lookup);
                this.angle.values.fields.length = 0;
                return this.angle.insertField(field, at);
            case 'tooltip':
                return this.tooltip.insertField(field, at);
        }
    }
    insertEncodedField(field, channel, at) {
        switch (channel) {
            case 'baseline':
                this.baseline.removeFromLookup(this.lookup);
                this.baseline.values.fields.length = 0;
                this.baseline.insertEncodedField(field, at);
                break;
            case 'color':
                this.color.removeFromLookup(this.lookup);
                this.color.values.fields.length = 0;
                this.color.insertEncodedField(field, at);
                break;
            case 'size':
                this.size.removeFromLookup(this.lookup);
                this.size.values.fields.length = 0;
                this.size.insertEncodedField(field, at);
                break;
            case 'label':
                this.label.insertEncodedField(field, at);
                break;
            case 'text':
                this.text.insertEncodedField(field, at);
                break;
            case 'detail':
                this.detail.insertEncodedField(field, at);
                break;
            case 'angle':
                this.angle.removeFromLookup(this.lookup);
                this.angle.values.fields.length = 0;
                this.angle.insertEncodedField(field, at);
                break;
            case 'tooltip':
                this.tooltip.insertEncodedField(field, at);
                break;
        }
    }
    removeFromLookup() {
        this.baseline.removeFromLookup(this.lookup);
        this.color.removeFromLookup(this.lookup);
        this.size.removeFromLookup(this.lookup);
        this.text.removeFromLookup(this.lookup);
        this.label.removeFromLookup(this.lookup);
        this.detail.removeFromLookup(this.lookup);
        this.angle.removeFromLookup(this.lookup);
        this.tooltip.removeFromLookup(this.lookup);
        // TODO remove field on (tooltip, path, etc) marks in the series
    }
    serialize() {
        var _a;
        const baseline = this.baseline.serialize();
        const color = this.color.serialize();
        const size = this.size.serialize();
        const text = this.text.serialize();
        const label = this.label.serialize();
        const detail = this.detail.serialize();
        const angle = this.angle.serialize();
        const tooltip = this.tooltip.serialize();
        return {
            type: this.type.serialize(),
            baseline,
            color,
            size,
            text,
            label,
            detail,
            angle,
            tooltip,
            options: { ...((_a = this.options) !== null && _a !== void 0 ? _a : {}) },
        };
    }
}
class BaseFieldArray {
    constructor() {
        this.contLast = true;
    }
    get length() {
        return this.fields.length;
    }
    insertField(field, at) {
        let index = -1;
        if (typeof at === 'string') {
            index = this.fields.findIndex((f) => f.id === at);
        }
        else if (typeof at === 'number') {
            index = this.fields.length > at ? at : -1;
        }
        if (this.contLast) {
            let firstCont = this.findFirstContinuousField();
            if (firstCont < 0) {
                firstCont = this.fields.length;
            }
            if (index >= 0) {
                if (index > 0 || field.isContinuous) {
                    if (field.isDiscrete && this.fields[index - 1].isContinuous) {
                        this.fields.splice(firstCont, 0, field);
                    }
                    else if (field.isContinuous && this.fields[index] && this.fields[index].isDiscrete) {
                        this.fields.splice(firstCont, 0, field);
                    }
                    else {
                        this.fields.splice(index, 0, field);
                    }
                }
                else {
                    this.fields.splice(index, 0, field);
                }
            }
            else {
                if (field.isDiscrete &&
                    this.fields.length > 0 &&
                    this.fields[this.fields.length - 1].isContinuous) {
                    this.fields.splice(firstCont, 0, field);
                }
                else {
                    this.fields.push(field);
                }
            }
        }
        else {
            if (index >= 0) {
                this.fields.splice(index, 0, field);
            }
            else {
                this.fields.push(field);
            }
        }
        field.parent = this;
    }
    removeField(id) {
        const index = this.fields.findIndex((f) => f.id === id);
        if (index >= 0) {
            this.fields.splice(index, 1);
        }
    }
    moveField(sourceId, destId) {
        const sourceIdx = this.fields.findIndex((f) => f.id === sourceId);
        const destIdx = this.fields.findIndex((f) => f.id === destId);
        if (sourceIdx >= 0 && destIdx >= 0) {
            this.fields.splice(destIdx, 0, this.fields.splice(sourceIdx, 1)[0]);
        }
    }
    /**
     * Finds a field in the array with the specified id or the last
     * field in the array with a matching group id.
     * @param id field or group id
     * @returns EncodedField
     */
    findById(id) {
        const byId = this.fields.find((f) => f.id === id);
        if (byId != null) {
            return byId;
        }
        const lastGroupIndex = this.findLastFieldIndexForGroup(id);
        return lastGroupIndex >= 0 ? this.fields[lastGroupIndex] : undefined;
    }
    findFirstFieldInGroupById(groupId) {
        return this.fields.find((f) => f.groupId === groupId);
    }
    /**
     * Finds the index of the field with the specified id or the
     * index of the last field in the group with the specified id
     * @param id field or group id
     * @returns number
     */
    findIndexById(id) {
        if (id) {
            const index = this.fields.findIndex((f) => f.id === id);
            return index >= 0 ? index : this.findLastFieldIndexForGroup(id);
        }
    }
    findLastFieldIndexForGroup(groupId) {
        if (groupId) {
            // Unfortunately, we don't have findLastIndex
            let index = this.fields.length;
            while (index--) {
                if (this.fields[index].groupId === groupId) {
                    return index;
                }
            }
        }
        return -1;
    }
    findFirstContinuousField() {
        return this.fields.findIndex((f) => f.isContinuous);
    }
    initLookup(lookup) {
        for (const field of this.fields) {
            lookup.set(field.id, field);
        }
    }
}
class EncodedSort {
    constructor(sort) {
        this.type = sort.type;
        this.source = sort.source;
        this.descending = sort.descending;
        switch (sort.type) {
            case SortType.NESTED: {
                this.field = sort.field;
                this.assertByField(sort.byField, sort.type);
                this.byField = new EncodedField(sort.byField, this);
                break;
            }
            case SortType.FIELD: {
                this.assertByField(sort.byField, sort.type);
                this.byField = new EncodedField(sort.byField, this);
                break;
            }
            case SortType.MANUAL: {
                this.items = sort.items;
                break;
            }
        }
    }
    assertByField(byField, sortType) {
        if (byField == null) {
            const type = sortType === SortType.FIELD ? 'ByField' : 'ByNested';
            throw new Error(`EncodedSort: "sort.byField" must be defined in a ${type} sort`);
        }
    }
    serialize() {
        switch (this.type) {
            case SortType.FIELD: {
                return {
                    type: this.type,
                    source: this.source,
                    descending: this.descending,
                    byField: this.byField.serialize(),
                };
            }
            case SortType.NESTED: {
                return {
                    type: this.type,
                    field: this.field,
                    source: this.source,
                    descending: this.descending,
                    byField: this.byField.serialize(),
                };
            }
            case SortType.MANUAL: {
                return {
                    type: this.type,
                    source: this.source,
                    descending: this.descending,
                    items: this.items,
                };
            }
            default: {
                return {
                    type: this.type,
                    source: this.source,
                    descending: this.descending,
                };
            }
        }
    }
}
class EncodedContinuousFilter extends EncodedField {
    constructor(filter, parent) {
        super(filter, parent);
        this.filterType = FilterType.Continuous;
        if (filter.range != null) {
            this.min = filter.range.min;
            this.max = filter.range.max;
        }
        this.includeMin = filter.includeMin;
        this.includeMax = filter.includeMax;
        this.includeNulls = filter.includeNulls;
    }
    serialize() {
        return {
            ...super.serialize(),
            range: {
                min: this.min,
                max: this.max,
            },
            includeMin: this.includeMin,
            includeMax: this.includeMax,
            includeNulls: this.includeNulls,
            filterType: this.filterType,
        };
    }
}
class EncodedContinuousDateFilter extends EncodedField {
    constructor(filter, parent) {
        super(filter, parent);
        this.filterType = FilterType.ContinuousDate;
        if (filter.range != null) {
            this.min = filter.range.min;
            this.max = filter.range.max;
        }
        this.includeMin = filter.includeMin;
        this.includeMax = filter.includeMax;
        this.includeNulls = filter.includeNulls;
    }
    serialize() {
        return {
            ...super.serialize(),
            range: {
                min: this.min,
                max: this.max,
            },
            includeMin: this.includeMin,
            includeMax: this.includeMax,
            includeNulls: this.includeNulls,
            filterType: this.filterType,
        };
    }
}
class EncodedRelativeDateFilter extends EncodedField {
    constructor(filter, parent) {
        super(filter, parent);
        this.filterType = FilterType.RelativeDate;
        this.from = { ...filter.from };
        this.to = { ...filter.to };
        this.weekStart = filter.weekStart;
        this.yearStart = filter.yearStart;
        this.includeNulls = filter.includeNulls;
        this.anchor = filter.anchor;
    }
    serialize() {
        return {
            ...super.serialize(),
            filterType: this.filterType,
            from: { ...this.from },
            to: { ...this.to },
            weekStart: this.weekStart,
            yearStart: this.yearStart,
            includeNulls: this.includeNulls,
            anchor: this.anchor,
        };
    }
}
class EncodedDiscreteFilter extends EncodedField {
    constructor(filter, parent) {
        super(filter, parent);
        this.filterType = FilterType.Discrete;
        this.selection = filter.selection
            ? {
                selected: filter.selection.selected.slice(),
                mode: filter.selection.mode,
            }
            : undefined;
        this.showRelevantValuesOnly = filter.showRelevantValuesOnly;
    }
    serialize() {
        return {
            ...super.serialize(),
            selection: {
                selected: this.selection.selected.slice(),
                mode: this.selection.mode,
            },
            filterType: this.filterType,
            showRelevantValuesOnly: this.showRelevantValuesOnly,
        };
    }
}
class EncodedDiscreteBooleanFilter extends EncodedField {
    constructor(filter, parent) {
        super(filter, parent);
        this.filterType = FilterType.DiscreteBoolean;
        this.selection = filter.selection
            ? {
                selected: filter.selection.selected.slice(),
                mode: filter.selection.mode,
            }
            : undefined;
        this.showRelevantValuesOnly = filter.showRelevantValuesOnly;
    }
    serialize() {
        return {
            ...super.serialize(),
            selection: {
                selected: this.selection.selected.slice(),
                mode: this.selection.mode,
            },
            filterType: this.filterType,
            showRelevantValuesOnly: this.showRelevantValuesOnly,
        };
    }
}
class EncodedTopByFieldFilter extends EncodedField {
    constructor(filter, parent) {
        var _a;
        super(filter, parent);
        this.filterType = FilterType.TopByField;
        const cond = filter.cond;
        this.cond = {
            operator: cond.operator,
            sequence: (_a = cond.sequence) !== null && _a !== void 0 ? _a : 
            // Existing filters won't have this set. Preserve the original
            // behavior of "FirstIsolate" when loading encodings that do not
            // have this explicitly configured
            EncTopByFieldCondition.Sequence.FirstIsolate,
            byField: new EncodedField(cond.byField, parent),
            limit: cond.limit,
        };
    }
    serialize() {
        return {
            ...super.serialize(),
            filterType: this.filterType,
            cond: {
                operator: this.cond.operator,
                sequence: this.cond.sequence,
                byField: this.cond.byField.serialize(),
                limit: this.cond.limit,
            },
        };
    }
}
function matchEncodedFilter(p) {
    return (f) => {
        switch (f.filterType) {
            case FilterType.Continuous:
                return p.Continuous(f);
            case FilterType.ContinuousDate:
                return p.ContinuousDate(f);
            case FilterType.RelativeDate:
                return p.RelativeDate(f);
            case FilterType.DiscreteBoolean:
                return p.DiscreteBoolean(f);
            case FilterType.Discrete:
                return p.Discrete(f);
            case FilterType.TopByField:
                return p.TopByField(f);
        }
    };
}
const newEncodedFilter = (parent) => {
    return matchEncodedFilter({
        Discrete: (f) => new EncodedDiscreteFilter(f, parent),
        DiscreteBoolean: (f) => new EncodedDiscreteBooleanFilter(f, parent),
        Continuous: (f) => new EncodedContinuousFilter(f, parent),
        ContinuousDate: (f) => new EncodedContinuousDateFilter(f, parent),
        RelativeDate: (f) => new EncodedRelativeDateFilter(f, parent),
        TopByField: (f) => new EncodedTopByFieldFilter(f, parent),
    });
};
class EncodedFilterArray extends BaseFieldArray {
    constructor(fields) {
        super();
        this.contLast = false;
        this.fields = (fields && fields.map(newEncodedFilter(this))) || [];
    }
    findBySource(source, discrete) {
        return this.fields.find((f) => f.formula === source && discrete === f.isDiscrete);
    }
    findIndexBySource(source, discrete) {
        return this.fields.findIndex((f) => f.formula === source && discrete === f.isDiscrete);
    }
    clear() {
        this.fields.length = 0;
    }
    removeBySource(source, discrete) {
        const index = this.findIndexBySource(source, discrete);
        if (index >= 0) {
            this.fields[index].detach();
            return this.fields[index];
        }
    }
    serialize() {
        return this.fields.map((f) => f.serialize());
    }
}
class EncodedSortArray {
    constructor(sorts) {
        this.sorts = sorts ? sorts.map((s) => new EncodedSort(s)) : [];
    }
    get length() {
        return this.sorts.length;
    }
    addSort(sort) {
        // only one sort per field
        this.removeBySource(sort.source);
        this.sorts.push(new EncodedSort({
            ...sort,
        }));
    }
    /**
     * @description Removes any sorts with a source matching the provided `source`
     */
    removeBySource(source) {
        this.removeBy((s) => s.source === source);
    }
    /**
     * @description Removes any sorts where the provided predicate function returns true
     */
    removeBy(pred) {
        this.sorts = this.sorts.filter((s) => !pred(s));
    }
    clear() {
        this.sorts.length = 0;
    }
    serialize() {
        return this.sorts.map((s) => s.serialize());
    }
}
class EncodedFieldArray extends BaseFieldArray {
    constructor(fields, lookup, contLast = true) {
        super();
        this.contLast = contLast;
        this.fields = (fields && fields.map((f) => new EncodedField(f, this))) || [];
        this.initLookup(lookup);
    }
    findLeftSibling(id) {
        const index = this.fields.findIndex((f) => f.id === id);
        if (index > 0) {
            return this.fields[index - 1];
        }
    }
    serialize() {
        return this.fields.map((f) => f.serialize());
    }
}
class EncodedDualXAxisFieldArray extends BaseFieldArray {
    constructor(dualXFields, source, lookup) {
        super();
        this.source = source;
        this.fields =
            (dualXFields &&
                dualXFields.map((f) => {
                    const sourceField = source.findById(f.x);
                    if (!sourceField) {
                        throw Error('field ' + f.x + ' not found in x axis');
                    }
                    return new EncodedDualAxisField(f, 'x', sourceField, this);
                })) ||
                [];
        this.initLookup(lookup);
    }
    removeFieldsByXRef(fieldId) {
        const fields = this.fields.filter((f) => f.x === fieldId);
        fields.forEach((f) => f.detach());
        return fields;
    }
    serialize() {
        return this.fields.map((f) => f.serialize());
    }
}
class EncodedDualYAxisFieldArray extends BaseFieldArray {
    constructor(dualYFields, source, lookup) {
        super();
        this.source = source;
        this.fields =
            (dualYFields &&
                dualYFields.map((f) => {
                    const sourceField = source.findById(f.y);
                    if (!sourceField) {
                        throw Error('field ' + f.y + ' not found in y axis');
                    }
                    return new EncodedDualAxisField(f, 'y', sourceField, this);
                })) ||
                [];
        this.initLookup(lookup);
    }
    removeFieldsByYRef(fieldId) {
        const fields = this.fields.filter((f) => f.y === fieldId);
        fields.forEach((f) => f.detach());
        return fields;
    }
    serialize() {
        return this.fields.map((f) => f.serialize());
    }
}
class EncodedMarkRoot {
    // Takes in a partial because it'll create defaults in that case
    constructor(root, lookup) {
        var _a;
        this.lookup = lookup;
        this.channel = '*';
        this.channel = root.channel || '*';
        this.series = new EncodedSeriesArray(root.series || [], lookup);
        this.discrete =
            this.channel === '*'
                ? new EncodedMarks(root.properties || {}, lookup)
                : new EncodedMarks({ type: (_a = root.properties) === null || _a === void 0 ? void 0 : _a.type }, lookup);
    }
    getMarksSummary() {
        const summary = new MarksSummary();
        if (this.series.fields.length) {
            const maxLength = this.series.fields.length;
            const types = Array.from(new Set(this.series.fields.map((f) => f.marks.type.value)));
            const angles = this.series.fields.map((f) => f.marks.angle.values.fields);
            const baselines = this.series.fields.map((f) => f.marks.baseline.values.fields);
            const colors = this.series.fields.map((f) => f.marks.color.values.fields);
            const details = this.series.fields.map((f) => f.marks.detail.values.fields);
            const labels = this.series.fields.map((f) => f.marks.label.values.fields);
            const sizes = this.series.fields.map((f) => f.marks.size.values.fields);
            const texts = this.series.fields.map((f) => f.marks.text.values.fields);
            const tooltips = this.series.fields.map((f) => f.marks.tooltip.values.fields);
            summary.type = types.length > 1 ? 'multiple' : types[0];
            summary.automatic = this.series.fields.every((f) => f.marks.type.automatic);
            [summary.angle, summary.angleIds] = this.generateMultiMarkSummary(angles, maxLength);
            [summary.baseline, summary.baselineIds] = this.generateMultiMarkSummary(baselines, maxLength);
            [summary.color, summary.colorIds] = this.generateMultiMarkSummary(colors, maxLength);
            [summary.detail, summary.detailIds] = this.generateMultiMarkSummary(details, maxLength);
            [summary.label, summary.labelIds] = this.generateMultiMarkSummary(labels, maxLength);
            [summary.size, summary.sizeIds] = this.generateMultiMarkSummary(sizes, maxLength);
            [summary.text, summary.textIds] = this.generateMultiMarkSummary(texts, maxLength);
            [summary.tooltip, summary.tooltipIds] = this.generateMultiMarkSummary(tooltips, maxLength);
        }
        else {
            summary.type = this.discrete.type.value;
            summary.automatic = this.discrete.type.automatic;
            [summary.angle, summary.angleIds] = this.generateMultiMarkSummary([this.discrete.angle.values.fields], 1);
            [summary.baseline, summary.baselineIds] = this.generateMultiMarkSummary([this.discrete.baseline.values.fields], 1);
            [summary.color, summary.colorIds] = this.generateMultiMarkSummary([this.discrete.color.values.fields], 1);
            [summary.detail, summary.detailIds] = this.generateMultiMarkSummary([this.discrete.detail.values.fields], 1);
            [summary.label, summary.labelIds] = this.generateMultiMarkSummary([this.discrete.label.values.fields], 1);
            [summary.size, summary.sizeIds] = this.generateMultiMarkSummary([this.discrete.size.values.fields], 1);
            [summary.text, summary.textIds] = this.generateMultiMarkSummary([this.discrete.text.values.fields], 1);
            [summary.tooltip, summary.tooltipIds] = this.generateMultiMarkSummary([this.discrete.tooltip.values.fields], 1);
        }
        return summary;
    }
    serialize() {
        const { type } = this.discrete;
        const properties = this.channel === '*'
            ? this.discrete
            : new EncodedMarks({
                type,
            }, this.lookup);
        return {
            channel: this.channel,
            series: this.series.serialize(),
            properties: properties.serialize(),
        };
    }
    generateMultiMarkSummary(array, maxLength) {
        // reducer will take a 2D array of fields and groups each row of equal fields
        // key is the first field in each row. This first item serves as the prototype for display
        const multiReducer = (coll, next) => {
            for (const field of next) {
                let added = false;
                for (const id in coll) {
                    if (EncodedField.matches(coll[id][0], field)) {
                        coll[id].push(field);
                        added = true;
                    }
                }
                if (!added) {
                    coll[field.id] = [field];
                }
            }
            return coll;
        };
        const fields = array.reduce(multiReducer, {});
        const results = {};
        // if any group have missing fields, they will be grouped under 'multiple'
        for (const id in fields) {
            if (fields[id].length !== maxLength) {
                if (!results['multiple']) {
                    results['multiple'] = [];
                }
                results['multiple'] = results['multiple'].concat(fields[id]);
            }
            else {
                results[id] = fields[id];
            }
        }
        const summary = [[], {}];
        for (const [id, encoded] of Object.entries(results)) {
            summary[1][id] = encoded.map((f) => f.id);
            if (id !== 'multiple') {
                summary[0].push(encoded[0].serialize());
            }
        }
        return summary;
    }
}
class EncodedSeries {
    constructor(series, lookup) {
        this.field = series.field;
        this.marks = new EncodedMarks(series.properties, lookup);
    }
    insertField(field, channel, at) {
        this.marks.insertField(field, channel, at);
    }
    serialize() {
        return {
            field: this.field,
            properties: this.marks.serialize(),
        };
    }
}
export class MarksSummary {
    constructor() {
        this.angle = [];
        this.baseline = [];
        this.color = [];
        this.detail = [];
        this.label = [];
        this.size = [];
        this.text = [];
        this.tooltip = [];
        this.angleIds = {};
        this.baselineIds = {};
        this.colorIds = {};
        this.detailIds = {};
        this.labelIds = {};
        this.sizeIds = {};
        this.textIds = {};
        this.tooltipIds = {};
        this.options = {};
    }
}
class EncodedSeriesArray {
    constructor(series, lookup) {
        this.fields = (series && series.map((s) => new EncodedSeries(s, lookup))) || [];
    }
    get hasPlaceholder() {
        return !this.fields.length;
    }
    insertField(seriesId, field, channel, at) {
        if (seriesId !== '*') {
            const series = this.fields.find((s) => s.field === seriesId);
            if (series) {
                return series.marks.insertField(field, channel, at);
            }
        }
    }
    newSeries(fieldId, lookup) {
        let series = this.fields.find((s) => s.field === fieldId);
        if (!series) {
            series = new EncodedSeries({ field: fieldId, properties: new EncodedMarks({}, lookup).serialize() }, lookup);
            this.fields.push(series);
        }
        return series;
    }
    removeField(fieldId) {
        const index = this.fields.findIndex((s) => s.field === fieldId);
        if (index >= 0) {
            const series = this.fields.splice(index, 1)[0];
            series.marks.removeFromLookup();
            return series;
        }
    }
    moveField(fieldId, toIndex) {
        const at = this.fields[toIndex];
        const index = this.fields.findIndex((s) => s.field === fieldId);
        if (index >= 0 && (at === null || at === void 0 ? void 0 : at.field) !== fieldId) {
            const series = this.fields.splice(index, 1)[0];
            const insertIndex = at != null ? this.fields.findIndex((s) => s === at) : this.fields.length;
            this.fields.splice(insertIndex, 0, series);
            return series;
        }
    }
    findById(id) {
        return this.fields.find((s) => s.field === id);
    }
    serialize() {
        return this.fields.map((s) => s.serialize());
    }
}
class EncodedFieldGroup {
    constructor(group) {
        this.id = group.id;
        this.type = group.type;
        this.name = group.name;
    }
    serialize() {
        return {
            id: this.id,
            type: this.type,
            name: this.name,
        };
    }
}
class EncodedFieldGroupArray {
    constructor(groups) {
        this.groups = groups ? groups.map((g) => new EncodedFieldGroup(g)) : [];
    }
    get length() {
        return this.groups.length;
    }
    /**
     * @description Adds a group
     */
    add(group) {
        this.groups.push(new EncodedFieldGroup({
            ...group,
        }));
    }
    /**
     * @description Removes group by id
     */
    removeById(groupId) {
        this.groups = this.groups.filter((g) => g.id !== groupId);
    }
    getById(groupId) {
        return this.groups.find((g) => g.id === groupId);
    }
    clear() {
        this.groups.length = 0;
    }
    serialize() {
        return this.groups.map((g) => g.serialize());
    }
}
export class Encoder {
    static newEncoder(encoding) {
        return new Encoder(encoding);
    }
    constructor(encoding) {
        this.encoding = encoding;
        this.fieldLookup = new Map();
        this.row = new EncodedFieldArray(encoding.row, this.fieldLookup);
        this.column = new EncodedFieldArray(encoding.column, this.fieldLookup);
        this.x = new EncodedFieldArray(encoding.x, this.fieldLookup);
        this.y = new EncodedFieldArray(encoding.y, this.fieldLookup);
        this.x2 = new EncodedDualXAxisFieldArray(encoding.x2, this.x, this.fieldLookup);
        this.y2 = new EncodedDualYAxisFieldArray(encoding.y2, this.y, this.fieldLookup);
        this.value = new EncodedFieldArray(encoding.value, this.fieldLookup);
        this.markRoot = new EncodedMarkRoot(encoding.marks || {}, this.fieldLookup);
        this.sort = new EncodedSortArray(encoding.sorts);
        this.filter = new EncodedFilterArray(encoding.filter);
        this.totals = new EncodedTotals(encoding.totals, this.fieldLookup);
        this.groups = new EncodedFieldGroupArray(encoding.groups);
    }
    addFilterField(field, at) {
        this.validateFilter(field);
        const discrete = field.variable.type === VariableType.DISCRETE;
        const index = this.filter.findIndexBySource(field.formula, discrete);
        if (index >= 0) {
            this.filter.removeBySource(field.formula, discrete);
        }
        const filter = newEncodedFilter(this.filter)(field);
        if (filter) {
            // Precedence: at -> index -> undefined
            this.filter.insertField(filter, at != null ? at : index >= 0 ? index : undefined);
        }
    }
    mergeFilterField(field) {
        var _a, _b;
        this.validateFilter(field);
        const discrete = field.variable.type === VariableType.DISCRETE;
        let existing = this.filter.findBySource(field.formula, discrete);
        if (existing == null) {
            this.addFilterField(field);
        }
        else {
            const encodedFilter = newEncodedFilter(this.filter)({ ...field });
            field = encodedFilter.serialize();
            switch (field.filterType) {
                case FilterType.Continuous:
                case FilterType.ContinuousDate: {
                    let checkIncludeMinMax = false;
                    // Convert existing relative date to continuous date for merging
                    if (existing.filterType === FilterType.RelativeDate) {
                        checkIncludeMinMax = true;
                        existing = newEncodedFilter(this.filter)({
                            ...toContinuousDateFilterField(existing.serialize()),
                        });
                    }
                    // Check if continuous overlaps
                    if (existing.filterType === FilterType.Continuous ||
                        existing.filterType === FilterType.ContinuousDate) {
                        let cMin = existing.min;
                        let cMax = existing.max;
                        // TODO: support includeMin and includeMax flags.
                        // TODO: don't mutate the field parameter
                        // Coalesce mins and maxes. This is to avoid doing comparisons
                        // against null, which is always considered smaller than a non-null.
                        // Null essentially means unbounded in this case, so setting min/max
                        // to the non-null value, while unintuitive, accomplishes the merge.
                        if (field.range.min == null || cMin == null) {
                            field.range.min = (_a = field.range.min) !== null && _a !== void 0 ? _a : cMin;
                            cMin = cMin !== null && cMin !== void 0 ? cMin : field.range.min;
                        }
                        if (field.range.max == null || cMax == null) {
                            field.range.max = (_b = field.range.max) !== null && _b !== void 0 ? _b : cMax;
                            cMax = cMax !== null && cMax !== void 0 ? cMax : field.range.max;
                        }
                        if (field.range.min == null && field.range.max == null) {
                            // no meaningful filter produced, just return
                            return;
                        }
                        else if (field.range.min == null) {
                            field.range.max = Math.min(field.range.max, cMax);
                        }
                        else if (field.range.max == null) {
                            field.range.min = Math.max(field.range.min, cMin);
                        }
                        else if (field.range.min <= cMax && cMin <= field.range.max) {
                            // take max of mins and mins of max
                            field.range.max = Math.min(field.range.max, cMax);
                            field.range.min = Math.max(field.range.min, cMin);
                        }
                        else {
                            throw new Error('Range filters must overlap');
                        }
                        // We took a min/max date from the relative date filter which is
                        // designed to be inclusive of the min/max values
                        if (checkIncludeMinMax) {
                            field.includeMin = field.range.min === existing.min ? true : field.includeMin;
                            field.includeMax = field.range.max === existing.max ? true : field.includeMax;
                        }
                        this.addFilterField(field);
                    }
                    else {
                        throw new Error('Filter types do not match');
                    }
                    break;
                }
                case FilterType.Discrete:
                case FilterType.DiscreteBoolean:
                    if (existing.filterType === FilterType.Discrete ||
                        existing.filterType === FilterType.DiscreteBoolean) {
                        /**
                         * Include + Include = Intersected Include
                         * Include + Exclude = Intersected Include
                         * Exclude + Exclude = Union Exclude
                         * All + Either = Either (Drop All filter)
                         */
                        if (field.selection.mode === SelectionMode.ALL) {
                            return; // An all filter is basically a nothing filter
                        }
                        else if (existing.selection.mode === SelectionMode.ALL) {
                            // do nothing, this will basically overwrite the existing ALL filter
                        }
                        else if (field.selection.mode === SelectionMode.INCLUDE &&
                            existing.selection.mode === SelectionMode.INCLUDE) {
                            const selected = new Set(existing.selection.selected);
                            field.selection.selected = field.selection.selected.filter((s) => selected.has(s));
                        }
                        else if (field.selection.mode === SelectionMode.EXCLUDE &&
                            existing.selection.mode === SelectionMode.EXCLUDE) {
                            field.selection.selected = Array.from(new Set([...field.selection.selected, ...existing.selection.selected]));
                        }
                        else {
                            // must be include + exclude
                            const include = field.selection.mode === SelectionMode.INCLUDE ? field : existing;
                            const exclude = new Set((field === include ? existing : field).selection.selected);
                            field.selection = {
                                mode: SelectionMode.INCLUDE,
                                selected: include.selection.selected.filter((s) => !exclude.has(s)),
                            };
                        }
                        this.addFilterField(field);
                    }
                    else {
                        throw new Error('Filter types do not match');
                    }
                    break;
                case FilterType.RelativeDate: {
                    // The logic for merging a continuous date filter with a relative
                    // date filter lives in the Continuous|ContinuousDate case. The goal
                    // here is to have the relative date filter be the "existing" filter
                    // then merge in the continuous date filter
                    const current = existing.serialize();
                    // Existing filter is already a realtive date, convert
                    // merge field to a continuous date then merge it
                    if (current.filterType === FilterType.RelativeDate) {
                        this.mergeFilterField(toContinuousDateFilterField(field));
                    }
                    // Existing filter is a continuous date, replace it with the
                    // relative date filter then merge in the continuous date
                    else if (current.filterType === FilterType.ContinuousDate) {
                        this.addFilterField(field);
                        this.mergeFilterField(current);
                    }
                    // Invalid types in combination
                    else {
                        throw new Error(`Filter types do not match: ${field.filterType} - ${existing.filterType}`);
                    }
                    break;
                }
                case FilterType.TopByField: {
                    // Replace
                    if (field.filterType === existing.filterType) {
                        this.addFilterField(field);
                        break;
                    }
                    throw new Error(`Filter types do not match: ${field.filterType} - ${existing.filterType}`);
                }
            }
        }
    }
    // Currently just enables adding multiple filters on the same formula
    appendFilterField(field, at) {
        const filter = newEncodedFilter(this.filter)(field);
        this.filter.insertField(filter, at);
    }
    addField(field, channel, at) {
        let encoded;
        switch (channel) {
            case 'row':
                encoded = new EncodedField(field, this.row);
                this.resetY();
                this.row.insertField(encoded, at);
                if (this.markRoot.channel === 'y' && encoded.isContinuous) {
                    const seriesProps = this.markRoot.getMarksSummary();
                    this.cloneMarks(encoded.id, seriesProps, this.markRoot.series.fields[0].marks);
                }
                break;
            case 'column':
                encoded = new EncodedField(field, this.column);
                this.resetX();
                this.column.insertField(encoded, at);
                if (this.markRoot.channel === 'x' && encoded.isContinuous) {
                    const seriesProps = this.markRoot.getMarksSummary();
                    this.cloneMarks(encoded.id, seriesProps, this.markRoot.series.fields[0].marks);
                }
                break;
            case 'value':
                if (field.formula !== SystemFieldName.NAMES && field.formula !== SystemFieldName.VALUES) {
                    encoded = new EncodedField(field, this.value);
                    this.value.insertField(encoded, at);
                }
                break;
            case 'filter':
                // TODO: Separate filter from base channels
                break;
        }
        // filters don't go in the field lookup since they should be keyed on formula
        if (encoded) {
            this.fieldLookup.set(encoded.id, encoded);
        }
    }
    addFieldToSeries(seriesId, field, channel, at) {
        let encoded;
        // Actual series id
        if (seriesId !== '*') {
            encoded = this.markRoot.series.insertField(seriesId, field, channel, at);
        }
        // Series is *
        else {
            // Mark root channel SHOULD match
            if (this.markRoot.channel === '*') {
                encoded = this.markRoot.discrete.insertField(field, channel, at);
            }
            else {
                throw new Error(`Cannot add field to "${channel}" on series "*", ` +
                    `current mark root channel is "${this.markRoot.channel}"`);
            }
        }
        if (encoded) {
            this.fieldLookup.set(encoded.id, encoded);
        }
    }
    addFieldToAllSeries(generateField, channel, at) {
        if (this.markRoot.series.fields.length) {
            for (const markSeries of this.markRoot.series.fields) {
                const markField = generateField();
                this.addFieldToSeries(markSeries.field, markField, channel);
            }
        }
        else {
            throw new Error('Must have series to add a field to a mark channel');
        }
    }
    toggleDualAxis(id) {
        const right = this.getField(id);
        if (right) {
            if (right.parent === this.x2 || right.parent === this.y2) {
                if (right.parent === this.x2) {
                    this.resetX();
                    const { x, ...dualField } = right.serialize();
                    const newField = new EncodedField(dualField, this.column);
                    const index = this.column.findIndexById(x);
                    this.column.insertField(newField, index + 1);
                }
                else {
                    this.resetY();
                    const { y, ...dualField } = right.serialize();
                    const newField = new EncodedField(dualField, this.row);
                    const index = this.row.findIndexById(y);
                    this.row.insertField(newField, index + 1);
                }
                right.detach();
                this.autoEncode();
            }
            else {
                // check if field already has a dual axis
                const axis = right.parent === this.y ? 'y' : 'x';
                const array = axis === 'x' ? this.x : this.y;
                const dest = axis === 'x' ? this.x2 : this.y2;
                const left = array.findLeftSibling(right.id);
                if (left && left.isContinuous) {
                    right.detach();
                    const dual = new EncodedDualAxisField(right.serialize(), axis, left, dest);
                    dest.insertField(dual);
                    this.fieldLookup.set(dual.id, dual);
                }
                else {
                    throw Error('Field has no left sibling with a continuous variable type.');
                }
            }
        }
    }
    /*
     * Returns an EncodedDualAxisPair containing the EncodedField and the EncodedDualAxisField which are
     * being swapped by this modification. This requires that the ModifyField is part of a dual axis
     * pair and that the order of the two fields is being swapped. All other cases return undefined.
     */
    getSwappedDualAxisPair(query) {
        if (query.channel !== 'row' && query.channel !== 'column') {
            return undefined;
        }
        const modifiedField = this.getField(query.id);
        if (!modifiedField) {
            return undefined;
        }
        const axisFieldArray = modifiedField.parent;
        const otherAxisFieldArray = this.getOppositeAxis(axisFieldArray);
        return modifiedField instanceof EncodedDualAxisField
            ? this.getPairFromDualAxisField(modifiedField, query.channel, query.at, otherAxisFieldArray)
            : this.getPairFromEncodedField(modifiedField, query.channel, query.at, otherAxisFieldArray);
    }
    getPairFromDualAxisField(dualAxisField, channel, insertAt, otherAxisFieldArray) {
        var _a;
        if (!dualAxisField) {
            return undefined;
        }
        // The modified field is a dual axis field. To swap, it must be moved before it's corresponding field on the other (x or y) axis.
        // Explicitly use the passed channel and not the dual axis ref here since the field may be moving to a different channel
        const refFieldId = channel === 'row' ? dualAxisField.y : dualAxisField.x;
        if (refFieldId !== undefined &&
            (insertAt === refFieldId ||
                insertAt === ((_a = otherAxisFieldArray.findFirstFieldInGroupById(refFieldId)) === null || _a === void 0 ? void 0 : _a.id))) {
            // This is a move of a dual axis field directly before its corresponding field. Return the pair.
            const primaryField = otherAxisFieldArray.findById(refFieldId);
            const primaryFields = primaryField.groupId
                ? this.findFieldsInGroup(primaryField.groupId)
                : [primaryField];
            return {
                primaryFields,
                secondaryFields: [dualAxisField],
            };
        }
    }
    getPairFromEncodedField(modifiedField, channel, insertAt, otherAxisFieldArray) {
        if (!otherAxisFieldArray || otherAxisFieldArray.length === 0) {
            return undefined;
        }
        const dualAxisFields = this.getAssociatedDualAxisFields(modifiedField, channel, otherAxisFieldArray);
        if (dualAxisFields.length > 0) {
            // Found the dual axis field which matches the field being modified. Check that the modified field is being moved directly
            // after the dual axis field. Note: if it is moved to the end of the list, then query.at is undefined.
            const encodedAxisArray = modifiedField.parent;
            if (!encodedAxisArray) {
                throw Error('Unexpected field array type');
            }
            const index = encodedAxisArray.findIndexById(modifiedField.id);
            if (index < 0) {
                throw Error(`Unable to find field for id:${modifiedField.id}`);
            }
            if ((index === encodedAxisArray.length - 1 && insertAt === undefined) ||
                (index < encodedAxisArray.length - 1 && insertAt === encodedAxisArray.fields[index + 1].id)) {
                const primaryField = encodedAxisArray.fields[index];
                return {
                    primaryFields: [primaryField],
                    secondaryFields: dualAxisFields,
                };
            }
        }
    }
    getOppositeAxis(axis) {
        if (axis === this.y) {
            return this.y2;
        }
        if (axis === this.y2) {
            return this.y;
        }
        if (axis === this.x) {
            return this.x2;
        }
        if (axis === this.x2) {
            return this.x;
        }
    }
    getAssociatedDualAxisFields(field, channel, dualAxisFieldArray) {
        return dualAxisFieldArray.fields.reduce((refFields, dualAxisField) => {
            var _a;
            const refFieldId = channel === 'row' ? dualAxisField.y : dualAxisField.x;
            const ref = (_a = field.groupId) !== null && _a !== void 0 ? _a : field.id;
            if (refFieldId === ref) {
                refFields.push(dualAxisField);
            }
            return refFields;
        }, []);
    }
    hasField(id) {
        return this.fieldLookup.has(id);
    }
    removeField(id) {
        var _a;
        const field = this.getField(id);
        const groupId = (_a = field === null || field === void 0 ? void 0 : field.groupId) !== null && _a !== void 0 ? _a : id;
        this.removeFieldFromRoles(field);
        this.checkRemoveFieldFromSorts(field);
        this.checkRemoveEmptyGroup(groupId);
        return field ? field.serialize() : undefined;
    }
    moveField(id, channel, at, seriesId) {
        const encodedField = this.getField(id);
        if (encodedField) {
            // Remove field from its current roles
            this.removeFieldFromRoles(encodedField);
            encodedField.groupId = undefined;
            this.checkRemoveEmptyGroup(id);
            // Add the field back to its new role.
            switch (channel) {
                case 'row':
                case 'column':
                case 'value':
                    this.addField(encodedField.serialize(), channel, at);
                    break;
                case 'filter':
                    // Can't use updateField to switch to filter channel because sensible default filter can't be inferred
                    // this.addFilterField({ ...field, filterType: FilterTypes.Selection }, at);
                    break;
                default: {
                    seriesId = this.markRoot.series.fields.length === 0 ? '*' : seriesId;
                    if (seriesId === '*') {
                        this.markRoot.discrete.insertEncodedField(encodedField, channel, at);
                    }
                    else {
                        const series = this.markRoot.series.findById(seriesId);
                        if (series != null) {
                            series.marks.insertEncodedField(encodedField, channel, at);
                        }
                        else {
                            // Add to all series
                            this.addFieldToAllSeries(() => {
                                return {
                                    ...encodedField,
                                    id: BField.GenerateID(),
                                };
                            }, channel, at);
                        }
                    }
                    // Clear nested sorts on move to mark channel
                    if (encodedField.isDiscrete) {
                        this.checkRemoveFieldFromSorts(encodedField);
                    }
                    break;
                }
            }
        }
    }
    removeFieldFromRoles(field) {
        if (field) {
            if (this.isInRowColumn(field)) {
                this.resetX();
                this.resetY();
                this.detachDualAxisByAxisField(field);
            }
            const id = field.id;
            field.detach();
            this.fieldLookup.delete(id);
            const series = this.markRoot.series.removeField(id); // attempt to remove from series, if exists
            if (series) {
                if (!this.markRoot.series.fields.length) {
                    // copy to placeholder series
                    this.markRoot.discrete = new EncodedMarks(series.marks.serialize(), this.fieldLookup);
                    this.markRoot.channel = '*';
                }
            }
        }
    }
    checkRemoveFieldFromSorts(field) {
        if (field) {
            const noMatchingDiscretes = !Array.from(this.fieldLookup.values()).some((f) => f.isDiscrete && f.formula === field.formula);
            // If there are no more discrete fields with this formula, remove it from the sort
            if (noMatchingDiscretes) {
                this.sort.removeBySource(field.formula);
            }
            // If the field has a nested sort we need to remove it explicitly
            // even if there are other discrete fields with the same formula
            else {
                this.sort.removeBy((s) => {
                    return s.type === SortType.NESTED && s.field === field.id;
                });
            }
        }
    }
    checkRemoveEmptyGroup(groupId) {
        const noMatchingGroupId = !Array.from(this.fieldLookup.values()).some((f) => f.groupId === groupId);
        if (noMatchingGroupId) {
            // No fields are part of this group, remove the group
            this.groups.removeById(groupId);
        }
    }
    detachDualAxisByAxisField(field) {
        const groupFields = field.groupId != null
            ? Array.from(this.fieldLookup.values()).filter((f) => f.groupId === field.groupId)
            : [];
        const refId = groupFields.length === 1 ? field.groupId : field.id;
        const x2Fields = this.x2.removeFieldsByXRef(refId);
        if (x2Fields.length > 0) {
            const fieldIndex = this.column.findIndexById(field.id) + 1;
            x2Fields.forEach((encodedField, index) => {
                const { x, ...dualField } = encodedField;
                const newField = new EncodedField(dualField, this.column);
                this.column.insertField(newField, fieldIndex + index);
                this.fieldLookup.set(newField.id, newField);
            });
        }
        else {
            const fieldIndex = this.row.findIndexById(field.id) + 1;
            const y2Fields = this.y2.removeFieldsByYRef(refId);
            y2Fields.forEach((encodedField, index) => {
                const { y, ...dualField } = encodedField;
                const newField = new EncodedField(dualField, this.row);
                this.row.insertField(newField, fieldIndex + index);
                this.fieldLookup.set(newField.id, newField);
            });
        }
    }
    replaceField(oldField, newField, dest) {
        const index = dest.findIndexById(oldField.id);
        oldField.detach();
        dest.insertField(newField, index);
        this.fieldLookup.set(newField.id, newField);
    }
    swapRowsColumns() {
        this.resetX();
        this.resetY();
        const rows = this.row.serialize();
        const columns = this.column.serialize();
        this.row = new EncodedFieldArray(columns, this.fieldLookup);
        this.column = new EncodedFieldArray(rows, this.fieldLookup);
        const x2 = this.resetAndSwapY2();
        const y2 = this.resetAndSwapX2();
        this.x2 = new EncodedDualXAxisFieldArray(x2, this.column, this.fieldLookup);
        this.y2 = new EncodedDualYAxisFieldArray(y2, this.row, this.fieldLookup);
        const grandTotals = this.totals.grand.serialize();
        this.totals.grand.column = grandTotals.row;
        this.totals.grand.row = grandTotals.column;
        if (this.markRoot.channel !== '*') {
            this.markRoot.channel = this.markRoot.channel === 'y' ? 'x' : 'y';
        }
        // Swap the frame direction for all fields.
        this.fieldLookup.forEach((field) => field.swapFrameAddress());
    }
    updateField(query) {
        var _a, _b, _c;
        const encodedField = this.getField(query.id);
        if (encodedField == null) {
            throw new Error(`Field with id ${query.id} not found`);
        }
        const prevField = encodedField.clone(encodedField.parent);
        ///
        ///
        // Frame must _explicitly_ change.
        // To unset a frame you must use setFrame or clearFrame
        if (query.frame != null) {
            // Is different from current
            if (!ExprFrame.equals(encodedField.frame, query.frame)) {
                encodedField.frame = query.frame;
            }
        }
        // Formula changed
        if (encodedField.formula !== ((_a = query.formula) !== null && _a !== void 0 ? _a : encodedField.formula)) {
            encodedField.formula = query.formula;
        }
        // Level changed
        if (encodedField.variable.level !== ((_b = query.variable) !== null && _b !== void 0 ? _b : encodedField.variable).level) {
            encodedField.variable.level = query.variable.level;
        }
        // Type changed
        if (encodedField.variable.type !== ((_c = query.variable) !== null && _c !== void 0 ? _c : encodedField.variable).type) {
            encodedField.variable.type = query.variable.type;
            // when changing variable type, probably need to rerun the encode function
            if ((encodedField.parent instanceof EncodedFieldArray ||
                encodedField.parent instanceof EncodedDualXAxisFieldArray ||
                encodedField.parent instanceof EncodedDualYAxisFieldArray) &&
                [this.row, this.y, this.y2, this.column, this.x, this.x2].indexOf(encodedField.parent) >= 0) {
                this.resetX();
                this.resetY();
                // delete series if switched to discrete
                if (encodedField.isDiscrete) {
                    if (this.markRoot.series.fields.length === 1) {
                        // Removing the last series will convert this to a '*' encoding
                        // in autoEncode->refreshSeries.
                        // Clone the marks and apply to the discrete channel so that mark channel
                        // settings aren't cleared.
                        const lastSeries = this.markRoot.series.fields[0];
                        this.markRoot.discrete = new EncodedMarks(lastSeries.marks.serialize(), this.fieldLookup);
                    }
                    this.markRoot.series.removeField(encodedField.id);
                    // detach/reattach any x2|y2 field shared by discrete field
                    this.detachDualAxisByAxisField(encodedField);
                    const groupId = encodedField.groupId;
                    encodedField.groupId = undefined;
                    if (groupId != null) {
                        this.checkRemoveEmptyGroup(groupId);
                    }
                }
                if (encodedField.parent instanceof EncodedDualYAxisFieldArray) {
                    const { y, ...dualField } = encodedField;
                    encodedField.detach();
                    const newField = new EncodedField(dualField, this.row);
                    this.row.insertField(newField, y);
                    this.fieldLookup.set(newField.id, newField);
                }
                else if (encodedField.parent instanceof EncodedDualXAxisFieldArray) {
                    const { x, ...dualField } = encodedField;
                    encodedField.detach();
                    const newField = new EncodedField(dualField, this.column);
                    this.column.insertField(newField, x);
                    this.fieldLookup.set(newField.id, newField);
                }
                else {
                    const arr = encodedField.parent;
                    const index = encodedField.parent.fields.indexOf(encodedField);
                    encodedField.detach();
                    arr.insertField(encodedField, index);
                }
                this.autoEncode();
            }
        }
        const dualAxisPair = this.getSwappedDualAxisPair(query);
        if (dualAxisPair) {
            // Swapping of a dual axis pair
            //
            // Three options:
            //   primary is field and secondary is field
            //      - swap fields
            //   primary is group and secondary is field (moving field above group)
            //      - field goes to x|y, group goes to x2|y2
            //   primary is field and secondary is group (moving field below group)
            //      - field goes to x2|y2, group goes to x|y
            //
            // Move all secondary fields to the primary axis before the primary field
            // Toggle dual axis or dual axis group on the secondary.
            //
            const firstPrimary = dualAxisPair.primaryFields[0];
            const primaryAxisArray = firstPrimary.parent;
            // x2|y2 moves to x|y.
            dualAxisPair.secondaryFields.forEach((field) => {
                field.detach();
                const newField = new EncodedField(field, query.channel === 'column' ? this.column : this.row);
                primaryAxisArray.insertField(newField, firstPrimary.id);
                this.fieldLookup.set(newField.id, newField);
            });
            // Toggle dual axis on the secondary fields
            if (firstPrimary.isInGroup) {
                this.toggleDualAxisOnGroup(firstPrimary.groupId);
            }
            else {
                this.toggleDualAxis(firstPrimary.id);
            }
        }
        else if (query.channel) {
            // Vegas move field
            this.moveField(query.id, query.channel, query.at, query.series);
            this.autoEncode();
        }
        else if (query.at) {
            // doing only a move
            // It's possible a full reset would be necessary here in the future
            const field = this.getField(query.id);
            const target = this.getField(query.at);
            if (field != null && target != null && field.parent === target.parent) {
                if ('length' in target.parent) {
                    target.parent.moveField(field.id, target.id);
                }
            }
        }
        // Remove the field's sort if necessary
        const updatedField = this.getField(query.id);
        if (updatedField != null) {
            if (
            // Changed from Discrete -> Continuous
            (prevField.isDiscrete && updatedField.isContinuous) ||
                // Is Discrete, formula did change
                (prevField.isDiscrete && prevField.formula !== updatedField.formula)) {
                this.checkRemoveFieldFromSorts(Object.assign(prevField, {
                    id: updatedField.id,
                }));
            }
        }
    }
    updateMarkType(type, automatic, fieldId) {
        if (fieldId) {
            if (fieldId !== '*') {
                const series = this.markRoot.series.findById(fieldId);
                if (series) {
                    this.convertDisallowedChannelsForMark(series.marks, series.field, type);
                    series.marks.type.value = type;
                    series.marks.type.automatic = automatic;
                }
            }
            else {
                this.convertDisallowedChannelsForMark(this.markRoot.discrete, '*', type);
                this.markRoot.discrete.type.value = type;
                this.markRoot.discrete.type.automatic = automatic;
            }
        }
        else {
            this.markRoot.series.fields.forEach((s) => {
                this.convertDisallowedChannelsForMark(s.marks, s.field, type);
                s.marks.type.value = type;
                s.marks.type.automatic = automatic;
            });
        }
    }
    updateMarkSize(size, axis) {
        if (axis) {
            const marks = this.getMark(axis);
            if (marks) {
                marks.size.value = size.value;
            }
        }
        else {
            this.markRoot.discrete.size.value = size.value;
            this.markRoot.series.fields.forEach((s) => {
                s.marks.size.value = size.value;
            });
        }
    }
    updateMarkColor(color, axis) {
        var _a, _b, _c, _d, _e, _f;
        if (axis) {
            const marks = this.getMark(axis);
            if (marks) {
                marks.color.value = (_a = color.value) !== null && _a !== void 0 ? _a : marks.color.value;
                marks.color.transparency = (_b = color.transparency) !== null && _b !== void 0 ? _b : marks.color.transparency;
                marks.color.target = (_c = color.target) !== null && _c !== void 0 ? _c : marks.color.target;
            }
        }
        else {
            this.markRoot.discrete.color.value = (_d = color.value) !== null && _d !== void 0 ? _d : this.markRoot.discrete.color.value;
            this.markRoot.discrete.color.transparency =
                (_e = color.transparency) !== null && _e !== void 0 ? _e : this.markRoot.discrete.color.transparency;
            this.markRoot.discrete.color.target = (_f = color.target) !== null && _f !== void 0 ? _f : this.markRoot.discrete.color.target;
            this.markRoot.series.fields.forEach((s) => {
                var _a, _b, _c;
                s.marks.color.value = (_a = color.value) !== null && _a !== void 0 ? _a : s.marks.color.value;
                s.marks.color.transparency = (_b = color.transparency) !== null && _b !== void 0 ? _b : s.marks.color.transparency;
                s.marks.color.target = (_c = color.target) !== null && _c !== void 0 ? _c : s.marks.color.target;
            });
        }
    }
    replaceMarkColor(color, axis) {
        if (axis) {
            const marks = this.getMark(axis);
            if (marks) {
                Object.assign(marks.color, color);
            }
        }
        else {
            Object.assign(this.markRoot.discrete.color, color);
            this.markRoot.series.fields.forEach((s) => {
                Object.assign(s.marks.color, color);
            });
        }
    }
    updateMarkLabel(label, axis) {
        // TODO: update label fields when they are available
        if (axis) {
            const marks = this.getMark(axis);
        }
    }
    updateMarkOptions(options, series) {
        const updateMarks = (marks) => {
            if (marks) {
                if (marks.options == null) {
                    marks.options = {};
                }
                const origOptions = marks.options;
                Object.assign(marks.options, {
                    [options.type]: {
                        ...origOptions[options.type],
                        ...options,
                    },
                });
            }
        };
        if (series) {
            const marks = this.getMark(series);
            updateMarks(marks);
        }
        else {
            updateMarks(this.markRoot.discrete);
            this.markRoot.series.fields.forEach((s) => {
                updateMarks(s.marks);
            });
        }
    }
    setTextDisplay(display, series) {
        if (series) {
            const marks = this.getMark(series);
            if (marks) {
                marks.text.display = display;
            }
        }
        else {
            this.markRoot.discrete.text.display = display;
            this.markRoot.series.fields.forEach((s) => {
                s.marks.text.display = display;
            });
        }
    }
    getMarksRollup() {
        return this.markRoot.getMarksSummary();
    }
    addSort(sort) {
        this.sort.addSort(sort);
    }
    removeSort(source) {
        this.sort.removeBySource(source);
    }
    clearSorts() {
        this.sort.clear();
    }
    removeFilter(source, discrete) {
        this.filter.removeBySource(source, discrete);
    }
    clearFilters() {
        this.filter.clear();
    }
    setFrame(fieldId, frame) {
        const encField = this.getField(fieldId);
        if (!encField) {
            throw new Error('Field not found');
        }
        encField.frame = frame;
    }
    /**
     * Creates a shared axis group from an existing field. Groups require
     * at least one member. Currently, we can only create a group from
     * a single field.
     * @param groupId - Unique group identifier
     * @param groupName - User friendly name
     * @param memberFieldId - Id of initial field with which to populate the group
     * @returns EncFieldGroup.SharedAxis
     * @throws if field does not exist
     */
    createSharedAxisGroup(groupId, groupName, memberFieldId) {
        const encodedField = this.getField(memberFieldId);
        if (!encodedField) {
            throw new Error(`createSharedAxisGroup: Field '${memberFieldId}' not found`);
        }
        // Capture any dual axis fields which are referencing this field
        const dualFields = this.findDualFields(encodedField);
        const sharedAxis = EncFieldGroup.SharedAxis.make(groupId, groupName);
        this.groups.add(sharedAxis);
        encodedField.groupId = groupId;
        // Reset the reference on the dual fields to point to the group now
        dualFields.forEach((f) => (f.ref = groupId));
        return sharedAxis;
    }
    /**
     * Adds a new field to the encoding and to the specified group
     * @param field EncField to add
     * @param groupId id of the group to which to make the new field a member
     * @param index Index in the group to add the field
     */
    addEncFieldToGroup(field, groupId, index) {
        if (!this.groups.getById(groupId)) {
            throw new Error(`addEncFieldToGroup: EncFieldGroup '${groupId}' not found`);
        }
        // Get the fields in this group
        const groupFields = this.findFieldsInGroup(groupId);
        if (groupFields.length === 0) {
            return;
        }
        const groupParent = groupFields[0].parent;
        const isDualGroup = groupParent === this.x2 || groupParent === this.y2;
        const isGroupOnX = groupParent === this.x || groupParent === this.x2;
        if (isDualGroup) {
            const relativeFields = isGroupOnX ? this.x : this.y;
            const dualFields = isGroupOnX ? this.x2 : this.y2;
            const dualPairId = groupFields[0].ref;
            const dualPair = relativeFields.findById(dualPairId);
            // Create a new dual axis field
            const encoded = new EncodedDualAxisField(field, isGroupOnX ? 'x' : 'y', dualPair, dualFields);
            encoded.groupId = groupId;
            this.fieldLookup.set(encoded.id, encoded);
            // Convert the index to a field and insert into dual fields
            const insertAt = this.findParentIndexForGroupMemberAt(index, dualFields, groupFields);
            dualFields.insertField(encoded, insertAt);
            if (this.isOnSeriesChannel(encoded)) {
                const seriesProps = this.markRoot.getMarksSummary();
                this.cloneMarks(encoded.id, seriesProps, this.markRoot.series.fields[0].marks);
            }
        }
        else {
            // Create a new encoded field
            const encoded = new EncodedField(field, isGroupOnX ? this.column : this.row);
            encoded.groupId = groupId;
            this.fieldLookup.set(encoded.id, encoded);
            const insertFields = isGroupOnX ? this.x : this.y;
            const insertAt = this.findParentIndexForGroupMemberAt(index, insertFields, groupFields);
            insertFields.insertField(encoded, insertAt);
            if (this.isOnSeriesChannel(encoded)) {
                const seriesProps = this.markRoot.getMarksSummary();
                this.cloneMarks(encoded.id, seriesProps, this.markRoot.series.fields[0].marks);
            }
        }
        this.validateSeries();
        this.syncSeriesOrderWithChannel();
        this.refreshSeries();
    }
    /**
     * Removes the specified field from its current group.
     * If this results in an empty group, then the group is also removed.
     *
     * If the field is on x|y, it is placed after the last remaining group member
     * if the channel and/or index are not specified.
     *
     * If the field is on x|y
     *   AND it is the last group member
     *   AND there is a field/group on x2|y2 referencing it,
     * then:
     *   the group is removed and the field/group on x2|y2 is changed to
     *   reference the field instead of the group.
     *
     * If the field is on x2|y2:
     *   AND this is the last field in the group
     * then:
     *   the group is removed, the field remains on x2|y2 and it continues to reference the same x|y.
     * else:
     *    if there are other group members,
     * then:
     *    the field is moved back to x|y and placed after the field/group which it was attached to.
     *
     * @param fieldId Id of field to remove from group
     * @param channel (Optional) if moving to a different channel. If not specified, then the field is move
     * to the current channel
     * @param index (Optional) position to place the field. By default it is moved immediately after the
     * group the field was removed from.
     */
    removeFieldFromGroup(fieldId, channel, index) {
        var _a;
        if (channel !== undefined && channel !== 'row' && channel !== 'column') {
            throw new Error(`removeFieldFromGroup: Channel '${channel}' is not supported`);
        }
        const encodedField = this.getField(fieldId);
        if (!encodedField) {
            throw new Error(`removeFieldFromGroup: Field '${fieldId}' not found`);
        }
        const groupId = encodedField.groupId;
        if (!groupId) {
            throw new Error(`removeFieldFromGroup: Field '${fieldId}' is not in a group`);
        }
        const groupFields = this.findFieldsInGroup(groupId);
        const isDual = this.isDual(encodedField);
        const isX = this.isInX(encodedField);
        const sameChannel = channel === undefined || (isX && channel === 'column') || (!isX && channel === 'row');
        const targetFieldArray = channel === 'row' ? this.y : channel === 'column' ? this.x : isX ? this.x : this.y;
        // Check if the field is being place between dual axis pairs. If so, it will break the
        // dual axis pair. Capture the fields which will need to be moved back to the x|y.
        let brokenDualPairFields = [];
        if (sameChannel && index !== undefined && index > 0) {
            const targetField = targetFieldArray.fields[index - 1];
            brokenDualPairFields =
                targetField != null ? this.findDualFields(targetField).filter((f) => f.id !== fieldId) : [];
        }
        // Save the current series
        const originalSeries = this.markRoot.series.removeField(encodedField.id);
        if (isDual && groupFields.length > 1) {
            // This field is part of a shared axis which is also a dual axis.
            // The group will remain and will continue to be a dual axis.
            // This field must be returned to x|y.
            encodedField.detach();
            // Create a new (non-dual) encoded field
            const encoded = new EncodedField(encodedField, isX ? this.column : this.row);
            encoded.groupId = undefined;
            this.fieldLookup.set(encoded.id, encoded);
            const dualPairId = groupFields[0].ref;
            if (sameChannel) {
                // Insert the field immediately after the field/group which it was
                // paired with if the index is not specified explicitly
                const insertAt = index === undefined ? targetFieldArray.findIndexById(dualPairId) + 1 : index;
                targetFieldArray.insertField(encoded, insertAt);
            }
            else {
                targetFieldArray.insertField(encoded, index);
            }
            // Restore or create new series
            this.restoreOrCreateSeries(encoded, originalSeries);
        }
        else {
            if (!isDual && groupFields.length === 1) {
                // This field is on x|y and the group will be disbanded when the
                // field is removed from the group.
                // There may be a field/group on x2|y2 referencing this group.
                // If so, we need to update those fields to reference the field
                // directly rather than the fields group.
                const dualFieldList = isX ? this.x2 : this.y2;
                // Modify the x|y ref on fields pointing to this group.
                dualFieldList.fields
                    .filter((f) => f.ref === groupId)
                    .forEach((f) => (f.ref = encodedField.id));
            }
            if (sameChannel) {
                // Move the field to the end of the group
                const insertIndex = index === undefined ? targetFieldArray.findLastFieldIndexForGroup(groupId) : index;
                if (insertIndex !== targetFieldArray.findIndexById(encodedField.id)) {
                    const parent = encodedField.parent;
                    // Shift the location of the field in the parent list
                    // moveField moves it to the index after the specified field
                    parent.moveField(encodedField.id, (_a = parent.fields[insertIndex]) === null || _a === void 0 ? void 0 : _a.id);
                }
                this.restoreOrCreateSeries(encodedField, originalSeries);
            }
            else {
                encodedField.detach();
                const parent = channel === 'row'
                    ? this.row
                    : channel === 'column'
                        ? this.column
                        : isX
                            ? this.column
                            : this.row;
                const encoded = new EncodedField(encodedField, parent);
                encoded.groupId = undefined;
                this.fieldLookup.set(encoded.id, encoded);
                targetFieldArray.insertField(encoded, index);
                this.restoreOrCreateSeries(encoded, originalSeries);
            }
            // Clear the group id
            encodedField.groupId = undefined;
        }
        // Insert any dual axis fields which were split during the move
        // back to the x|y axis.
        brokenDualPairFields.forEach((f, i) => {
            f.detach();
            const newField = new EncodedField(f, targetFieldArray);
            targetFieldArray.insertField(newField, targetFieldArray.findIndexById(fieldId) + 1 + i);
            this.fieldLookup.set(newField.id, newField);
        });
        // Sync the series order to match
        this.syncSeriesOrderWithChannel();
        // If the group will be empty, remove it.
        this.checkRemoveEmptyGroup(groupId);
    }
    /**
     * Handles moving an existing field into a group
     * @param fieldId id of existing field to move into group
     * @param groupId id of group field into
     * @param index Index in the group to add the field
     */
    moveEncFieldToGroup(fieldId, groupId, index) {
        var _a;
        const encField = this.getField(fieldId);
        if (!encField) {
            throw new Error(`moveEncFieldToGroup: Field '${fieldId}' not found`);
        }
        if (!this.groups.getById(groupId)) {
            throw new Error(`moveEncFieldToGroup: EncFieldGroup '${groupId}' not found`);
        }
        // Get the fields in this group
        const groupFields = this.findFieldsInGroup(groupId);
        const isGroupOnDualAxis = this.isDual(groupFields[0]);
        const isGroupOnX = this.isInX(groupFields[0]);
        // Capture the current field information
        const series = this.markRoot.series.removeField(fieldId);
        const originalGroupId = encField.groupId;
        // Detach the field
        encField.detach();
        // Capture any dual pairs which may need to be moved back to the x|y axis
        const dualPairs = this.findDualFields(encField);
        const refId = (_a = encField.groupId) !== null && _a !== void 0 ? _a : encField.id;
        // Relative fields are the fields on x|y.
        const relativeFields = isGroupOnX ? this.x : this.y;
        if (isGroupOnDualAxis) {
            const groupRelativeRefId = groupFields[0].ref;
            const dualFields = isGroupOnX ? this.x2 : this.y2;
            const relative = relativeFields.findById(groupRelativeRefId);
            if (relative != null) {
                // The relative field exists so create a new DualAxis field
                // and insert into the x2|y2 field array.
                const encoded = new EncodedDualAxisField(encField, isGroupOnX ? 'x' : 'y', relative, dualFields);
                encoded.groupId = groupId;
                this.fieldLookup.set(encoded.id, encoded);
                // Convert the index to a field.
                const insertAt = this.findParentIndexForGroupMemberAt(index, dualFields, groupFields);
                dualFields.insertField(encoded, insertAt);
                this.restoreOrCreateSeries(encoded, series);
            }
            else {
                // There is no relative which means the moved field must be the
                // only relative field. In this case, the entire group needs to
                // move back to the x|y.
                // An EncodedDualAxisField requires a relative so instead
                // just create a regular EncodedField and add to the list of
                // dual fields. All the the fields are going to be converted
                // to EncodedFields anyway. We just need to insert the field
                // into the correct index in the group before the move.
                //
                const newField = new EncodedField(encField, relativeFields);
                relativeFields.insertField(newField);
                this.fieldLookup.set(newField.id, newField);
                newField.groupId = groupId;
                dualPairs.splice(index, 0, newField);
                this.restoreOrCreateSeries(newField, series);
            }
            this.checkMoveDualPairsToRelativeAxis(refId, relativeFields, dualPairs);
        }
        else {
            // Group is not on x2|y2
            // Create a new field for the group
            const encoded = new EncodedField(encField, isGroupOnX ? this.column : this.row);
            encoded.groupId = groupId;
            const insertAt = this.findParentIndexForGroupMemberAt(index, relativeFields, groupFields);
            relativeFields.insertField(encoded, insertAt);
            this.fieldLookup.set(encoded.id, encoded);
            this.restoreOrCreateSeries(encoded, series);
            this.checkMoveDualPairsToRelativeAxis(refId, relativeFields, dualPairs);
        }
        // Clean up any empty groups
        if (originalGroupId != null) {
            this.checkRemoveEmptyGroup(originalGroupId);
        }
        // Fix up the series and order
        this.validateSeries();
        this.syncSeriesOrderWithChannel();
    }
    /**
     * Removes the group and all of the fields in the group.
     * @param groupId Id of the group to remove
     */
    removeEncFieldGroup(groupId) {
        if (!this.groups.getById(groupId)) {
            throw new Error(`removeEncFieldGroup: EncFieldGroup '${groupId}' not found`);
        }
        // Get the fields in this group
        const groupFields = this.findFieldsInGroup(groupId);
        if (groupFields.length === 0) {
            return;
        }
        const groupParent = groupFields[0].parent;
        const isGroupDual = groupParent === this.x2 || groupParent === this.y2;
        if (!isGroupDual) {
            const isGroupOnX = groupParent === this.x || groupParent === this.x2;
            // Check if field is dual axis relative to any other fields
            const dualAxisArray = isGroupOnX ? this.x2 : this.y2;
            // Find all of the fields that point to this group
            const dualFields = dualAxisArray.fields.filter((f) => f.ref === groupId);
            if (dualFields.length > 0) {
                // Move all of these fields back to the primary axis
                const moveToParent = isGroupOnX ? this.x : this.y;
                const insertIndex = moveToParent.findIndexById(groupId) + 1;
                dualFields.forEach((f, index) => {
                    f.detach();
                    const encoded = new EncodedField(f, isGroupOnX ? this.column : this.row);
                    moveToParent.insertField(encoded, insertIndex + index);
                    this.fieldLookup.set(encoded.id, encoded);
                });
            }
        }
        // Remove all of the fields associated with this group
        groupFields.forEach((f) => this.removeField(f.id));
        this.autoEncode();
        // Remove the group
        this.groups.removeById(groupId);
    }
    /**
     * Removes a grouping without removing the group members.
     * In general the fields remain in the same location as before
     * with some exceptions:
     *   - If the group is on x2|y2, then the dual axis pair is broken.
     *     All of the fields in the group move back to x|y immediately
     *     after the field/group it was paired with.
     *   - If the group is on x|y and there is a field or group on x2|y2
     *     that references it, then all of the dual fields move back to
     *     x|y immediately after the last field in this group.
     *
     * @param groupId
     */
    disbandGroup(groupId) {
        if (!this.groups.getById(groupId)) {
            throw new Error(`disbandGroup: EncFieldGroup ${groupId} not found`);
        }
        // Get the fields in this group
        const groupFields = this.findFieldsInGroup(groupId);
        if (groupFields.length === 0) {
            return;
        }
        const groupParent = groupFields[0].parent;
        const isGroupDual = groupParent === this.x2 || groupParent === this.y2;
        const isGroupOnX = groupParent === this.x || groupParent === this.x2;
        const moveToParent = isGroupOnX ? this.x : this.y;
        // If on x2|y2, move to x|y right after dual axis pair.
        if (isGroupDual) {
            const dualRef = groupFields[0].ref;
            // Find index of last element with this ref.
            const insertIndex = moveToParent.findIndexById(dualRef) + 1;
            if (insertIndex <= 0) {
                throw new Error('Unable to locate dual axis relative');
            }
            groupFields.forEach((f, index) => {
                f.detach();
                const encoded = new EncodedField(f, isGroupOnX ? this.column : this.row);
                encoded.groupId = undefined;
                moveToParent.insertField(encoded, insertIndex + index);
                this.fieldLookup.set(encoded.id, encoded);
            });
        }
        // If x|y and something refs this, move the dual axis pair to x|y right after the end of group
        else {
            const dualAxisArray = isGroupOnX ? this.x2 : this.y2;
            // Find all of the fields that point to this group
            const dualFields = dualAxisArray.fields.filter((f) => f.ref === groupId);
            if (dualFields.length > 0) {
                // Move all of these fields back to the primary axis
                const insertIndex = moveToParent.findIndexById(groupId) + 1;
                dualFields.forEach((f, index) => {
                    f.detach();
                    const encoded = new EncodedField(f, isGroupOnX ? this.column : this.row);
                    moveToParent.insertField(encoded, insertIndex + index);
                    this.fieldLookup.set(encoded.id, encoded);
                });
            }
            // Just clear the group id and remove the group
            groupFields.forEach((f) => (f.groupId = undefined));
        }
        // Now remove the group entirely
        this.groups.removeById(groupId);
    }
    /**
     * Moves all of the field in the specified group to the new location.
     * Special handling is required when moving a group which is part of a dual
     * axis. For most cases, the dual axis linkage is broken. It is only preserved
     * when swapping the dual axis fields/groups. Swapping is moving the dual
     * axis (x2|y2) immediately before the relative (x|y) or moving the relative (x|y)
     * immediately after the dual (x2|y2).
     *
     * @param groupId Id of the group to move
     * @param channel Channel ('row' or 'column') to move the group to.
     * @param at fieldId to add the group BEFORE or undefined to add to the end of the list
     */
    moveGroup(groupId, channel, at) {
        if (channel === 'filter' || channel === 'value') {
            throw new Error('moveGroup: Groups are not supported in filters or values');
        }
        // Get the fields in this group
        const groupFields = this.findFieldsInGroup(groupId);
        if (groupFields.length === 0) {
            return;
        }
        if (at != null && at === groupFields[0].id) {
            // Moving the group to the same position. Nothing to do.
            return;
        }
        const isGroupOnX = this.isInX(groupFields[0]);
        const relativeAxisArray = isGroupOnX ? this.x : this.y;
        const dualAxisArray = isGroupOnX ? this.x2 : this.y2;
        const isChannelChange = (isGroupOnX && channel === 'row') || (!isGroupOnX && channel === 'column');
        const isGroupOnDualAxis = this.isDual(groupFields[0]);
        // Group is 'dual relative' if there is an x2|y2 field/group that references it.
        const isGroupDualRelative = !isGroupOnDualAxis && dualAxisArray.fields.some((f) => f.ref === groupId);
        const channelFields = channel === 'column' ? this.x : this.y;
        const atIndex = at != null ? channelFields.findIndexById(at) : undefined;
        if (isGroupOnDualAxis) {
            // It is a swap if the atIndex === relative field or group start
            const ref = groupFields[0].ref;
            const groupStartIndex = relativeAxisArray.fields.findIndex((f) => f.groupId === ref || f.id === ref);
            const isSwap = !isChannelChange && groupStartIndex === atIndex;
            if (isSwap) {
                const relativeFields = relativeAxisArray.fields.filter((f) => f.groupId === ref || f.id === ref);
                // x2|y2 moves to x|y. This is the group being moved.
                groupFields.forEach((field) => {
                    field.detach();
                    const newField = new EncodedField(field, this.row);
                    relativeAxisArray.insertField(newField, at);
                    this.fieldLookup.set(newField.id, newField);
                    this.setGroupId(field.id, groupId);
                });
                // x|y moves to x2|y2. This may be a field or group
                if (relativeFields[0].isInGroup) {
                    this.toggleDualAxisOnGroup(relativeFields[0].groupId);
                }
                else {
                    this.toggleDualAxis(relativeFields[0].id);
                    this.autoEncode();
                }
            }
            else {
                // If this isn't a swap then we need to break the dual axis
                this.toggleDualAxisOnGroup(groupId);
                const newGroupFields = this.findFieldsInGroup(groupId);
                this.moveGroupFields(newGroupFields, groupId, channel, at);
            }
        }
        else if (isGroupDualRelative) {
            // This is x|y so the 'at' is relative to the fields in the parent array
            const dualFields = dualAxisArray.fields.filter((f) => f.ref === groupId);
            // If the at references one of the dual fields, then this is a move between
            // the dual axis pair, so do nothing.
            if (at != null && dualFields.some((f) => f.id === at)) {
                return;
            }
            // It is a swap if the 'at' is immediately after the last field in the group or undefined if there
            // is nothing else in the channel
            const groupIndex = relativeAxisArray.findLastFieldIndexForGroup(groupId);
            const isSwap = !isChannelChange &&
                (atIndex != null
                    ? atIndex === groupIndex + 1
                    : groupIndex === relativeAxisArray.length - 1);
            // x2|y2 moves to x|y. The x2|y2 may be a group or a single field
            if (dualFields[0].isInGroup) {
                this.toggleDualAxisOnGroup(dualFields[0].groupId);
            }
            else {
                this.toggleDualAxis(dualFields[0].id);
                this.autoEncode();
            }
            // Now actually move the fields
            this.moveGroupFields(groupFields, groupId, channel, at);
            if (isSwap) {
                // x|y moves to x2|y2. In this case, we know it's a group
                this.toggleDualAxisOnGroup(groupId);
            }
        }
        else {
            // No special handling necessary. Just move the group fields
            this.moveGroupFields(groupFields, groupId, channel, at);
        }
    }
    /**
     * Converts a group from relative (x|y) to dual axis (x2|y2) or reverse.
     *
     * @param groupId
     */
    toggleDualAxisOnGroup(groupId) {
        const groupFields = this.findFieldsInGroup(groupId);
        if (groupFields.length === 0) {
            return;
        }
        const isGroupOnX = this.isInX(groupFields[0]);
        const isGroupDual = this.isDual(groupFields[0]);
        if (isGroupDual) {
            // We are removing a dual axis. We need to move all of the
            // fields back to the x|y axis at the correct location.
            if (isGroupOnX) {
                this.resetX();
                groupFields.forEach((groupField, index) => {
                    const { x, ...dualField } = groupField.serialize();
                    groupField.detach();
                    const newField = new EncodedField(dualField, this.column);
                    this.fieldLookup.set(newField.id, newField);
                    const fieldIndex = this.column.findIndexById(x);
                    this.column.insertField(newField, fieldIndex + 1 + index);
                });
            }
            else {
                this.resetY();
                groupFields.forEach((groupField, index) => {
                    const { y, ...dualField } = groupField.serialize();
                    groupField.detach();
                    const newField = new EncodedField(dualField, this.row);
                    this.fieldLookup.set(newField.id, newField);
                    const fieldIndex = this.row.findIndexById(y);
                    this.row.insertField(newField, fieldIndex + 1 + index);
                });
            }
            this.autoEncode();
        }
        else {
            // We are creating a dual axis. Move fields to x2|y2.
            const axis = isGroupOnX ? 'x' : 'y';
            const array = axis === 'x' ? this.x : this.y;
            const dest = axis === 'x' ? this.x2 : this.y2;
            const left = array.findLeftSibling(groupFields[0].id);
            if (left && left.isContinuous) {
                const leftIndex = array.fields.findIndex((f) => f.id === left.id);
                const siblingIds = dest.fields
                    .map((f) => { var _a; return (_a = f.x) !== null && _a !== void 0 ? _a : f.y; })
                    .map((id) => array.fields.findIndex((f) => { var _a; return id === ((_a = f.groupId) !== null && _a !== void 0 ? _a : f.id); }));
                siblingIds.push(leftIndex);
                siblingIds.sort((a, b) => a - b);
                const insertAt = siblingIds.indexOf(leftIndex);
                groupFields.forEach((groupField, index) => {
                    groupField.detach();
                    const dual = new EncodedDualAxisField(groupField.serialize(), axis, left, dest);
                    // Insert at the correct location based on the other dual fields
                    dest.insertField(dual, insertAt + index);
                    this.fieldLookup.set(dual.id, dual);
                });
                this.syncSeriesOrderWithChannel();
            }
            else {
                throw Error('Field has no left sibling with a continuous variable type.');
            }
        }
    }
    moveGroupFields(groupFields, groupId, channel, at) {
        groupFields.forEach((field) => {
            this.moveFieldWithSeries(field, at, channel === 'row' ? this.y : this.x);
            this.setGroupId(field.id, groupId);
        });
        this.syncSeriesOrderWithChannel();
        this.autoEncode();
    }
    /**
     * Very specific helper function which will move a set of dual axis (x2|y2) fields
     * back to the relative (x|y) axis if there is no longer any field on the x|y which
     * is the reference pair.
     *
     * @param refId
     * @param targetArray
     * @param dualFields
     */
    checkMoveDualPairsToRelativeAxis(refId, targetArray, dualFields) {
        // We only want to move the x2|y2 fields back to the x|y if there is no longer
        // any field/group on the x|y to which the field references.
        // Every field in the target array must not be a x|y reference for this field
        if (targetArray.fields.every((f) => { var _a; return refId !== ((_a = f.groupId) !== null && _a !== void 0 ? _a : f.id); })) {
            // Move these fields back to x|y.
            dualFields.forEach((f) => {
                f.detach();
                const newField = new EncodedField(f, targetArray);
                targetArray.insertField(newField);
                this.fieldLookup.set(newField.id, newField);
            });
        }
    }
    /**
     * Returns the index of the field in the parent field array corresponding to the
     * field at the index in the group. If index is undefined, then the returned
     * value is after the end of the group.
     */
    findParentIndexForGroupMemberAt(index, parent, groupFields) {
        if (index < 0) {
            // The UI should never do this. But if it happens, treat it as the
            // beginning of the group.
            index = 0;
        }
        return index == null || index >= groupFields.length
            ? parent.findIndexById(groupFields[groupFields.length - 1].id) + 1
            : parent.findIndexById(groupFields[index].id);
    }
    moveFieldWithSeries(field, at, target) {
        // Capture the series
        const series = this.markRoot.series.removeField(field.id);
        const parent = field.parent;
        field.detach();
        if (parent !== target) {
            this.resetX();
            this.resetY();
            if (target === this.y) {
                this.row.insertField(field, at);
            }
            else if (target === this.x) {
                this.column.insertField(field, at);
            }
        }
        else {
            // Need to move to target.
            target.insertField(field, at);
        }
        this.fieldLookup.set(field.id, field);
        this.restoreOrCreateSeries(field, series);
    }
    restoreOrCreateSeries(field, series) {
        if (this.isOnSeriesChannel(field)) {
            if (series) {
                this.markRoot.series.fields.push(series);
            }
            else {
                this.cloneMarks(field.id, this.markRoot.getMarksSummary(), this.markRoot.series.fields[0].marks);
            }
        }
    }
    setGroupId(fieldId, groupId) {
        const encField = this.getField(fieldId);
        if (!encField) {
            throw new Error(`Field ${fieldId} not found`);
        }
        encField.groupId = groupId;
    }
    findDualFields(encodedField) {
        var _a;
        const parent = encodedField.parent;
        if (parent !== this.x && parent !== this.y) {
            return [];
        }
        const isX = parent === this.x || parent === this.x2;
        const dualAxisArray = isX ? this.x2 : this.y2;
        const ref = (_a = encodedField.groupId) !== null && _a !== void 0 ? _a : encodedField.id;
        return dualAxisArray.fields.filter((f) => f.ref === ref);
    }
    /*
     * Returns the fields in the specified group in the order
     * in which they appear in the respective channel
     */
    findFieldsInGroup(groupId) {
        return [...this.x.fields, ...this.y.fields, ...this.x2.fields, ...this.y2.fields].filter((f) => f.groupId === groupId);
    }
    autoEncode() {
        let contCount = this.x.length ? this.x.length : 0;
        for (let ii = this.column.length - 1; ii >= 0; ii--) {
            const c = this.column.fields[ii];
            if (c.isDiscrete) {
                if (contCount <= 0) {
                    this.column.fields.pop();
                    this.x.fields.push(c);
                    c.parent = this.x;
                }
                break;
            }
            this.column.fields.pop();
            this.x.fields.unshift(c);
            c.parent = this.x;
            contCount++;
        }
        contCount = this.y.length;
        for (let ii = this.row.length - 1; ii >= 0; ii--) {
            const r = this.row.fields[ii];
            if (r.isDiscrete) {
                if (contCount <= 0) {
                    this.row.fields.pop();
                    this.y.fields.push(r);
                    r.parent = this.y;
                }
                break;
            }
            this.row.fields.pop();
            this.y.fields.unshift(r);
            r.parent = this.y;
            contCount++;
        }
        this.refreshSeries();
        // Ensure that all series are minimally valid
        this.validateSeries();
    }
    getAllSiblingFields(id) {
        const field = this.getField(id);
        if (field != null) {
            const parent = field.parent;
            if (parent instanceof EncodedFieldArray ||
                parent instanceof EncodedSortArray ||
                parent instanceof EncodedFilterArray) {
                return parent.serialize();
            }
        }
        return undefined;
    }
    getFieldById(id) {
        const field = this.getField(id);
        return field != null ? field.serialize() : undefined;
    }
    enableGrandTotal(direction) {
        this.totals.grand.enable(direction);
    }
    disableGrandTotal(direction) {
        this.totals.grand.disable(direction);
    }
    serialize() {
        const row = this.row.serialize();
        const column = this.column.serialize();
        const x = this.x.serialize();
        const y = this.y.serialize();
        const x2 = this.x2.serialize();
        const y2 = this.y2.serialize();
        const value = this.value.serialize();
        const markRoot = this.markRoot.serialize();
        const totals = this.totals.serialize();
        return {
            row,
            column,
            x,
            y,
            x2,
            y2,
            value,
            marks: markRoot,
            ...(this.encoding.options && Object.keys(this.encoding.options).length
                ? { options: this.encoding.options }
                : {}),
            sorts: this.sort.serialize(),
            filter: this.filter.serialize(),
            totals,
            groups: this.groups.serialize(),
        };
    }
    convertDisallowedChannelsForMark(marks, axis, type) {
        // When switching mark types, need to get all channels that are being
        // removed and convert fields to a valid channel (default is Detail)
        if (AllowedMarkChannels[marks.type.value]) {
            const convertChannels = AllowedMarkChannels[marks.type.value].filter((e) => {
                return AllowedMarkChannels[type]
                    ? AllowedMarkChannels[type].indexOf(e) === -1
                    : AllowedAutoMarkChannels.indexOf(e) === -1;
            });
            if (convertChannels.length) {
                convertChannels.forEach((channel) => {
                    if (marks[channel].values.length) {
                        // for each marks[channel].values
                        // remove and readd to detail channel
                        const fieldList = marks[channel].values.fields.map((f) => f.id);
                        fieldList.forEach((id) => {
                            const field = marks[channel].values.findById(id);
                            this.removeField(id);
                            this.addFieldToSeries(axis, field, 'detail');
                        });
                    }
                });
            }
        }
    }
    cloneMarks(field, summary, baseMarks) {
        const series = this.markRoot.series.newSeries(field, this.fieldLookup);
        if (summary.type !== 'multiple') {
            series.marks.type.value = summary.type;
            series.marks.type.automatic = summary.automatic;
        }
        else {
            series.marks.type.value = null;
            series.marks.type.automatic = false;
        }
        series.marks.options = { ...baseMarks.options };
        summary.angle.forEach((f) => {
            series.marks.angle.insertEncodedField(baseMarks.angle.values.findById(f.id).clone(series.marks.angle.values));
        });
        summary.baseline.forEach((f) => {
            series.marks.baseline.insertEncodedField(baseMarks.baseline.values.findById(f.id).clone(series.marks.baseline.values));
            series.marks.baseline.value = baseMarks.baseline.value;
        });
        summary.color.forEach((f) => {
            series.marks.color.insertEncodedField(baseMarks.color.values.findById(f.id).clone(series.marks.color.values));
            series.marks.color.target = baseMarks.color.target;
            series.marks.color.transparency = baseMarks.color.transparency;
            series.marks.color.value = baseMarks.color.value;
        });
        summary.detail.forEach((f) => {
            series.marks.detail.insertEncodedField(baseMarks.detail.values.findById(f.id).clone(series.marks.detail.values));
        });
        summary.label.forEach((f) => {
            series.marks.label.insertEncodedField(baseMarks.label.values.findById(f.id).clone(series.marks.label.values));
        });
        summary.size.forEach((f) => {
            series.marks.size.insertEncodedField(baseMarks.size.values.findById(f.id).clone(series.marks.size.values));
            series.marks.size.value = baseMarks.size.value;
        });
        summary.text.forEach((f) => {
            series.marks.text.insertEncodedField(baseMarks.text.values.findById(f.id).clone(series.marks.text.values));
        });
        summary.tooltip.forEach((f) => {
            series.marks.tooltip.insertEncodedField(baseMarks.tooltip.values.findById(f.id).clone(series.marks.tooltip.values));
        });
    }
    refreshSeries() {
        const xCont = this.x.fields.filter((field) => field.isContinuous).concat(this.x2.fields);
        const yCont = this.y.fields.filter((field) => field.isContinuous).concat(this.y2.fields);
        const xMeasures = xCont.filter((f) => !f.isDimension);
        const yMeasures = yCont.filter((f) => !f.isDimension);
        let originalSummary;
        let originalMarks;
        // reset series if there's only a placeholder
        if ((xCont.length || yCont.length) && this.markRoot.series.hasPlaceholder) {
            originalSummary = this.markRoot.getMarksSummary();
            originalMarks = this.markRoot.discrete;
        }
        // reset series if there's only one item and there is another axis with more than 1 cont field
        // or if there are no cont fields on the current channel anymore
        if (this.markRoot.series.fields.length === 1 &&
            (xCont.length > 1 ||
                yCont.length > 1 ||
                (this.markRoot.channel === 'x' &&
                    ((!xCont.length && yCont.length) || (!xMeasures.length && yMeasures.length))) ||
                (this.markRoot.channel === 'y' &&
                    ((!yCont.length && xCont.length) || (!yMeasures.length && xMeasures.length))))) {
            originalSummary = this.markRoot.getMarksSummary();
            originalMarks = this.markRoot.series.removeField(this.markRoot.series.fields[0].field).marks;
        }
        // add new fields to series if continuous fields count on the current channel has changed size
        if (this.markRoot.series.fields.length > 1) {
            let newSeries = [];
            if (this.markRoot.channel === 'x' && xCont.length > this.markRoot.series.fields.length) {
                newSeries = xCont.filter((field) => !this.markRoot.series.fields.find((series) => series.field === field.id));
            }
            else if (this.markRoot.channel === 'y' &&
                yCont.length > this.markRoot.series.fields.length) {
                newSeries = yCont.filter((field) => !this.markRoot.series.fields.find((series) => series.field === field.id));
            }
            newSeries.forEach((field) => {
                this.markRoot.series.newSeries(field.id, this.fieldLookup);
            });
        }
        if (!this.markRoot.series.fields.length) {
            if (xCont.length > yCont.length || xMeasures.length > yMeasures.length) {
                this.markRoot.channel = 'x';
                for (const x of xCont) {
                    if (originalSummary) {
                        this.cloneMarks(x.id, originalSummary, originalMarks);
                    }
                    else {
                        this.markRoot.series.newSeries(x.id, this.fieldLookup);
                    }
                }
            }
            else if (yCont.length) {
                this.markRoot.channel = 'y';
                for (const y of yCont) {
                    if (originalSummary) {
                        this.cloneMarks(y.id, originalSummary, originalMarks);
                    }
                    else {
                        this.markRoot.series.newSeries(y.id, this.fieldLookup);
                    }
                }
            }
            else {
                this.markRoot.channel = '*';
            }
        }
        if (this.markRoot.channel !== '*') {
            this.syncSeriesOrderWithChannel();
        }
    }
    validateSeries() {
        // Default any null marks to Text.
        if (this.markRoot.series.fields.length) {
            for (const series of this.markRoot.series.fields) {
                if (series.marks.type.value == null) {
                    this.updateMarkType(MarkType.Text, true, series.field);
                }
            }
        }
        else if (this.markRoot.discrete.type.value == null) {
            this.updateMarkType(MarkType.Text, true, '*');
        }
    }
    syncSeriesOrderWithChannel() {
        // Keep series order in sync with axis.
        const arr = this.markRoot.series.fields;
        const axisFields = this.markRoot.channel === 'y'
            ? [...this.y.fields, ...this.y2.fields]
            : [...this.x.fields, ...this.x2.fields];
        // Sort the series to match the field order
        arr.sort((series1, series2) => {
            const index1 = axisFields.findIndex((f) => f.id === series1.field);
            const index2 = axisFields.findIndex((f) => f.id === series2.field);
            return index1 - index2;
        });
    }
    isInRowColumn(field) {
        if (field.parent instanceof EncodedFieldArray) {
            return [this.row, this.column, this.x, this.y, this.x2, this.y2].indexOf(field.parent) >= 0;
        }
        return false;
    }
    isInX(field) {
        return field.parent === this.x || field.parent === this.x2;
    }
    isInY(field) {
        return field.parent === this.y || field.parent === this.y2;
    }
    isDual(field) {
        return field.parent === this.x2 || field.parent === this.y2;
    }
    isOnSeriesChannel(field) {
        return ((this.isInX(field) && this.markRoot.channel === 'x') ||
            (this.isInY(field) && this.markRoot.channel === 'y'));
    }
    getField(id) {
        return this.fieldLookup.get(id) || this.filter.findById(id);
    }
    getMark(axis) {
        if (axis) {
            const series = this.markRoot.series.findById(axis);
            if (series) {
                return series.marks;
            }
        }
        return this.markRoot.discrete;
    }
    resetY() {
        for (const field of this.y.fields) {
            this.row.insertField(field);
        }
        this.y.fields.length = 0;
    }
    resetX() {
        for (const field of this.x.fields) {
            this.column.insertField(field);
        }
        this.x.fields.length = 0;
    }
    resetAndSwapY2() {
        const reference = [];
        for (const field of this.y2.fields) {
            const { y, ...y2Field } = field.serialize();
            const newField = {
                ...y2Field,
                x: y,
            };
            reference.push(newField);
        }
        this.y2.fields.length = 0;
        return reference;
    }
    resetAndSwapX2() {
        const reference = [];
        for (const field of this.x2.fields) {
            const { x, ...x2Field } = field.serialize();
            const newField = {
                ...x2Field,
                y: x,
            };
            reference.push(newField);
        }
        this.x2.fields.length = 0;
        return reference;
    }
    validateFilter(field) {
        switch (field.filterType) {
            case FilterType.Continuous:
            case FilterType.ContinuousDate:
            case FilterType.RelativeDate:
                if (field.variable.type !== VariableType.CONTINUOUS) {
                    throw new Error('Continuous filter types must have a continuous variable type');
                }
                break;
            case FilterType.TopByField: {
                const limit = field.cond.limit | 0;
                if (limit < 0 || limit !== field.cond.limit) {
                    throw new Error(`TopByField filter limit must be a positive integer, saw ${field.cond.limit}`);
                }
                break;
            }
            default:
                if (field.variable.type !== VariableType.DISCRETE) {
                    throw new Error('Discrete filter types must have a discrete variable type');
                }
                break;
        }
    }
}
